import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const COMPANY_NAME = 'Electronic City for IT Services';
const PRIMARY_COLOR = [41, 128, 185];
const GRAY_COLOR = [149, 165, 166];

const PRODUCTIVE_HOURS_LABELS = {
  daily: 'Productive Hours/Day',
  monthly: 'Productive Hours/Month',
  hourly: 'Productive Hours (Hourly)',
};

const formatCurrency = (value, symbol = 'OMR') => {
  return `OMR ${parseFloat(value || 0)?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
};

// ── Shared Calculation Engine ─────────────────────────────────────────────────
// All raw numeric computations live here so PDF and Excel always produce
// identical results. Nothing is rounded until display time.
// CRITICAL: unitCost is always resolved as a raw number via explicit parseFloat
// fallback chain to prevent stale string values from sessionStorage.

const resolveUnitCost = (mat) => {
  // Priority: unitCostOmr (explicit OMR value) → unitCost (fallback)
  // Always parseFloat to strip any accidental string formatting
  const raw = mat?.unitCostOmr ?? mat?.unitCost;
  return parseFloat(raw) || 0;
};

const computeMaterialLineItems = (selectedMaterials, calculations) => {
  const oh = (calculations?.overheadPercentage || 0) / 100;
  const cont = (calculations?.contingencyPercentage || 0) / 100;
  const margin = (calculations?.marginPercentage || 0) / 100;

  return (selectedMaterials || [])?.map(mat => {
    const qty = parseFloat(mat?.quantity) || 0;
    // Always resolve unit cost via the explicit fallback chain
    const unitCost = resolveUnitCost(mat);
    const wastePct = parseFloat(mat?.wastePercentage) || 0;
    const wasteMultiplier = 1 + wastePct / 100;

    // Cost section: includes waste
    const costTotal = qty * unitCost * wasteMultiplier;

    // Sell section: Sell Price/Unit = unitCost × wasteMultiplier × (1+oh) × (1+cont) / (1-margin)
    const unitWithWaste = unitCost * wasteMultiplier;
    const unitWithOverhead = unitWithWaste * (1 + oh);
    const unitWithContingency = unitWithOverhead * (1 + cont);
    const sellUnitCost = margin < 1
      ? unitWithContingency / (1 - margin)
      : unitWithContingency * (1 + margin);
    // Sell Total = Qty × Sell Price/Unit (waste already baked into sellUnitCost)
    const sellTotal = sellUnitCost * qty;

    // Sell Price/Unit/Month = sell price per single unit (monthly equivalent = unit cost)
    const sellPricePerUnitMonth = sellUnitCost;

    return {
      id: mat?.masterId || mat?.id || '—',
      name: mat?.itemLabel || mat?.name || mat?.item || 'Material',
      qty,
      unitCost,           // raw unit price from DB (OMR)
      wastePct,
      costTotal,
      sellUnitCost,
      sellTotal,
      sellPricePerUnitMonth,
    };
  });
};

const computeEngineerLineItems = (selectedEngineers, calculations, deliveryModeMultiplier = 1) => {
  const oh = (calculations?.overheadPercentage || 0) / 100;
  const cont = (calculations?.contingencyPercentage || 0) / 100;
  const margin = (calculations?.marginPercentage || 0) / 100;

  return (selectedEngineers || [])?.map(eng => {
    const nationality = eng?.nationality || 'expat';
    const timeUnit = eng?.timeUnit || 'hours';
    const count = parseFloat(eng?.count) || 0;
    const hours = parseFloat(eng?.hours) || 0;
    const costPerHour = parseFloat(eng?.costPerHour) || 0;
    const duration = parseFloat(eng?.duration) || 1;

    // Monthly Salary (OMR) — use directly from master data field
    const monthlySalary = parseFloat(eng?.monthlySalary) || 0;

    const prorateMonthly = (val) => {
      if (!val) return 0;
      if (timeUnit === 'days') return val / 30;
      if (timeUnit === 'hours') return val / 30 / 9;
      return val;
    };

    const rawSpf = nationality === 'omani' ? (eng?.spfValue ?? ((eng?.monthlySalary || 0) * ((eng?.spfPercentage || 22.5) / 100))) : 0;
    const rawHI = eng?.healthInsuranceEnabled ? (parseFloat(eng?.healthInsuranceValue) || 0) : 0;
    const spfAmount = rawSpf * duration * count;
    const hiAmount = rawHI * duration * count;

    // Line Total = Monthly Salary (OMR) × count × duration + SPF (OMR) + Health Ins. (OMR)
    const monthlySalaryTotal = prorateMonthly(monthlySalary) * duration * count;
    const costTotal = monthlySalaryTotal + spfAmount + hiAmount;

    // Unit Rate base: Cost/Hr + SPF/Hr (Omani only). HI is excluded from unit rate.
    const spfPerHour = (count * hours) > 0 ? spfAmount / (count * hours) : 0;
    const unitRateBase = costPerHour * deliveryModeMultiplier + (nationality === 'omani' ? spfPerHour : 0);
    const hrWithOverhead = unitRateBase * (1 + oh);
    const hrWithContingency = hrWithOverhead * (1 + cont);
    const sellCostPerHour = margin < 1
      ? hrWithContingency / (1 - margin)
      : hrWithContingency * (1 + margin);
    const sellTotal = sellCostPerHour * count * hours;

    // Sell Price/Unit/Month = sell price per hour × monthly hours per single engineer
    const sellPricePerUnitMonth = sellCostPerHour * hours;

    return {
      id: eng?.masterId || eng?.id || '—',
      name: eng?.roleLabel || eng?.name || 'Engineer',
      specialization: eng?.specialization || '—',
      nationality: nationality === 'omani' ? 'Omani' : 'Expat',
      count,
      hours,
      costPerHour,
      monthlySalary: monthlySalaryTotal,
      spfValue: spfAmount,
      healthInsurance: hiAmount,
      healthInsuranceEnabled: eng?.healthInsuranceEnabled,
      costTotal,
      sellCostPerHour,
      sellTotal,
      sellPricePerUnitMonth,
    };
  });
};

const computeLicenseLineItems = (selectedLicenses, calculations) => {
  const oh = (calculations?.overheadPercentage || 0) / 100;
  const cont = (calculations?.contingencyPercentage || 0) / 100;
  const margin = (calculations?.marginPercentage || 0) / 100;

  return (selectedLicenses || [])?.map(lic => {
    const unitCostRaw = lic?.type === 'perpetual' ? parseFloat(lic?.oneTimeCost || 0) : parseFloat(lic?.monthlyCost || 0);
    const qty = parseFloat(lic?.quantity) || 1;
    const duration = parseFloat(lic?.duration) || 1;
    const costTotal = lic?.type === 'perpetual'
      ? unitCostRaw * qty
      : unitCostRaw * qty * duration;

    const unitWithOverhead = unitCostRaw * (1 + oh);
    const unitWithContingency = unitWithOverhead * (1 + cont);
    const sellUnitCost = margin < 1
      ? unitWithContingency / (1 - margin)
      : unitWithContingency * (1 + margin);
    const sellTotal = lic?.type === 'perpetual'
      ? sellUnitCost * qty
      : sellUnitCost * qty * duration;

    // Sell Price/Unit/Month:
    //   Subscription: sellUnitCost (already monthly per license)
    //   Perpetual: amortized over duration months
    const sellPricePerUnitMonth = lic?.type === 'perpetual'
      ? (duration > 0 ? sellUnitCost / duration : sellUnitCost)
      : sellUnitCost;

    return {
      id: lic?.masterId || lic?.id || '—',
      name: lic?.softwareLabel || lic?.name || 'License',
      vendor: lic?.vendor || '—',
      type: lic?.type === 'perpetual' ? 'Perpetual' : 'Subscription',
      qty,
      duration,
      unitCostRaw,
      costTotal,
      sellUnitCost,
      sellTotal,
      sellPricePerUnitMonth,
    };
  });
};

const computeOutsourceLineItems = (selectedOutsource, calculations) => {
  const oh = (calculations?.overheadPercentage || 0) / 100;
  const cont = (calculations?.contingencyPercentage || 0) / 100;
  const margin = (calculations?.marginPercentage || 0) / 100;

  return (selectedOutsource || [])?.map(r => {
    const costPerHour = parseFloat(r?.costPerHour) || 0;
    const qty = parseFloat(r?.quantity) || 1;
    const hours = parseFloat(r?.hours) || 0;
    const duration = parseFloat(r?.duration) || 1;
    const timeUnit = r?.timeUnit || 'hours';
    const spfCost = parseFloat(r?.spfCost) || 0;
    const healthInsurance = r?.healthInsuranceEnabled ? (parseFloat(r?.healthInsuranceValue) || 0) : 0;

    // Monthly Salary (OMR) — use directly from master data field
    const monthlySalaryRaw = parseFloat(r?.monthlySalary) || 0;

    const prorateMonthly = (val) => {
      if (!val) return 0;
      if (timeUnit === 'days') return val / 30;
      if (timeUnit === 'hours') return val / 30 / 9;
      return val;
    };

    // spfCost is already the total SPF (Monthly SPF Value × Qty × Duration)
    // stored by OutsourceSelector — do NOT re-prorate or re-multiply
    const spfProrated = spfCost;
    const hiProrated = r?.healthInsuranceEnabled ? (parseFloat(healthInsurance) || 0) * duration * qty : 0;
    const monthlySalaryTotal = prorateMonthly(monthlySalaryRaw) * duration * qty;

    // Line Total = Monthly Salary (OMR) + SPF (OMR) + Health Ins. (OMR)
    const costTotal = monthlySalaryTotal + spfProrated + hiProrated;

    // Unit Rate base: Cost/Hr + SPF/Hr (Omani only). HI is excluded from unit rate.
    const isOmani = r?.nationality === 'omani';
    const spfPerHour = (qty * hours) > 0 ? spfProrated / (qty * hours) : 0;
    const unitRateBase = costPerHour + (isOmani ? spfPerHour : 0);
    const hrWithOverhead = unitRateBase * (1 + oh);
    const hrWithContingency = hrWithOverhead * (1 + cont);
    const sellCostPerHour = margin < 1
      ? hrWithContingency / (1 - margin)
      : hrWithContingency * (1 + margin);
    const sellTotal = sellCostPerHour * qty * hours;

    // Sell Price/Unit/Month = sell price per hour × monthly hours per single resource
    const sellPricePerUnitMonth = sellCostPerHour * hours;

    const timeUnitLabel = r?.timeUnit === 'days' ? 'Days' : r?.timeUnit === 'months' ? 'Months' : 'Hours';
    const roleLabel = r?.role
      ? r?.role?.charAt(0)?.toUpperCase() + r?.role?.slice(1)
      : (r?.roleSkill || r?.specialization || '—');

    return {
      id: r?.masterId || r?.id || '—',
      name: r?.name || r?.resourceName || 'Resource',
      vendor: r?.vendor || '—',
      roleLabel,
      nationality: r?.nationality === 'omani' ? 'Omani' : 'Expat',
      monthlySalary: monthlySalaryTotal,
      duration,
      timeUnitLabel,
      qty,
      hours,
      costPerHour,
      spfCost: spfProrated,
      healthInsurance: hiProrated,
      healthInsuranceEnabled: r?.healthInsuranceEnabled,
      isOmani,
      costTotal,
      sellCostPerHour,
      sellTotal,
      sellPricePerUnitMonth,
    };
  });
};

// ── Build debug rows for all resource types ───────────────────────────────────
// Returns flat array: { productId, productName, unitPrice, qty, lineTotal, category }
const buildDebugRows = (matItems, engItems, licItems, outItems) => {
  const rows = [];
  let running = 0;

  (matItems || [])?.forEach(m => {
    running += m?.sellTotal;
    rows?.push({
      category: 'Material',
      productId: String(m?.id),
      productName: m?.name,
      unitPrice: m?.sellUnitCost,   // sell price per unit (post-margin)
      unitPriceDB: m?.unitCost,     // raw DB price
      qty: m?.qty,
      lineTotal: m?.sellTotal,
      running,
    });
  });

  (engItems || [])?.forEach(e => {
    running += e?.sellTotal;
    rows?.push({
      category: 'Engineer',
      productId: String(e?.id),
      productName: e?.name,
      unitPrice: e?.sellCostPerHour,
      unitPriceDB: e?.costPerHour,
      qty: e?.count * e?.hours,
      lineTotal: e?.sellTotal,
      running,
    });
  });

  (licItems || [])?.forEach(l => {
    running += l?.sellTotal;
    rows?.push({
      category: 'License',
      productId: String(l?.id),
      productName: l?.name,
      unitPrice: l?.sellUnitCost,
      unitPriceDB: l?.unitCostRaw,
      qty: l?.type === 'Perpetual' ? l?.qty : l?.qty * l?.duration,
      lineTotal: l?.sellTotal,
      running,
    });
  });

  (outItems || [])?.forEach(o => {
    running += o?.sellTotal;
    rows?.push({
      category: 'Outsource',
      productId: String(o?.id),
      productName: o?.name,
      unitPrice: o?.sellCostPerHour,
      unitPriceDB: o?.costPerHour,
      qty: o?.qty * o?.hours,
      lineTotal: o?.sellTotal,
      running,
    });
  });

  return rows;
};

// ── Pre-export validation ─────────────────────────────────────────────────────
const validateTotals = (calculations, matItems, engItems, licItems, outItems) => {
  const errors = [];

  // Only validate Materials and Licenses (their formula is unchanged)
  if (matItems?.length > 0) {
    const sumMat = matItems?.reduce((s, m) => s + m?.costTotal, 0);
    const reported = calculations?.totalMaterialsCost || 0;
    if (Math.abs(sumMat - reported) > 0.001) {
      errors?.push(`Materials: sum of line items (${formatCurrency(sumMat)}) does not match reported total (${formatCurrency(reported)}).`);
    }
  }

  if (licItems?.length > 0) {
    const sumLic = licItems?.reduce((s, l) => s + l?.costTotal, 0);
    const reported = calculations?.totalLicensesCosts || 0;
    if (Math.abs(sumLic - reported) > 0.001) {
      errors?.push(`Licenses: sum of line items (${formatCurrency(sumLic)}) does not match reported total (${formatCurrency(reported)}).`);
    }
  }

  return errors;
};

// ── Compute summary totals from line items ────────────────────────────────────
// Engineers and Outsource use Monthly Salary + SPF + Health Ins. formula.
// Summary totals are derived from these line totals + overhead/contingency/margin from config.
const computeSummaryTotals = (engItems, matItems, licItems, outItems, calculations) => {
  const engCostSum = (engItems || [])?.reduce((s, e) => s + e?.costTotal, 0);
  const matCostSum = (matItems || [])?.reduce((s, m) => s + m?.costTotal, 0);
  const licCostSum = (licItems || [])?.reduce((s, l) => s + l?.costTotal, 0);
  const outCostSum = (outItems || [])?.reduce((s, o) => s + o?.costTotal, 0);

  const directCosts = engCostSum + matCostSum + licCostSum + outCostSum;
  const oh = (calculations?.overheadPercentage || 0) / 100;
  const cont = (calculations?.contingencyPercentage || 0) / 100;
  const margin = (calculations?.marginPercentage || 0) / 100;

  const overheadCosts = directCosts * oh;
  const contingencyCosts = (directCosts + overheadCosts) * cont;
  const totalCost = directCosts + overheadCosts + contingencyCosts;
  const sellPrice = margin < 1 ? totalCost / (1 - margin) : totalCost * (1 + margin);
  const grossProfit = sellPrice - totalCost;
  const profitMarginPercentage = sellPrice > 0 ? (grossProfit / sellPrice) * 100 : 0;

  return {
    engCostSum, matCostSum, licCostSum, outCostSum,
    directCosts, overheadCosts, contingencyCosts,
    totalCost, sellPrice, grossProfit, profitMarginPercentage,
  };
};

const ExportModal = ({ isOpen, onClose, calculations, deliveryMode, selectedEngineers, selectedLicenses, selectedMaterials, selectedOutsource, config, pricingBasis, savedPtcName, savedPtcNumber }) => {
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exportMode, setExportMode] = useState('internal');
  const [isExporting, setIsExporting] = useState(false);
  const [estimateTitle, setEstimateTitle] = useState('');
  const [estimateNo, setEstimateNo] = useState('');
  const [ptcNumber, setPtcNumber] = useState('');
  const [estimateDate, setEstimateDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [errors, setErrors] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    if (isOpen) {
      const counter = parseInt(localStorage.getItem('calcEstimateCounter') || '0', 10) + 1;
      localStorage.setItem('calcEstimateCounter', counter);
      const padded = String(counter)?.padStart(4, '0');
      setEstimateNo(`Calc-${padded}`);
      setEstimateTitle(savedPtcName || '');
      setPtcNumber(savedPtcNumber || '');
      setErrors({});
      setValidationErrors([]);
    }
  }, [isOpen, savedPtcName, savedPtcNumber]);

  // ── Compute line items once (shared by validate, debug table, PDF, Excel) ──
  const getComputedItems = () => {
    const deliveryModeMultipliers = { onsite: 1.0, remote: 0.85, hybrid: 0.92 };
    const modeMultiplier = deliveryModeMultipliers?.[deliveryMode] ?? 1.0;
    const matItems = computeMaterialLineItems(selectedMaterials, calculations);
    const engItems = computeEngineerLineItems(selectedEngineers, calculations, modeMultiplier);
    const licItems = computeLicenseLineItems(selectedLicenses, calculations);
    const outItems = computeOutsourceLineItems(selectedOutsource, calculations);
    return { matItems, engItems, licItems, outItems, modeMultiplier };
  };

  const validate = () => {
    const newErrors = {};
    if (!estimateTitle?.trim()) newErrors.estimateTitle = 'PTC Name is required. Please save the estimate first before exporting.';
    if (!ptcNumber?.trim()) newErrors.ptcNumber = 'PTC Number is required. Please save the estimate first before exporting.';
    setErrors(newErrors);
    if (Object.keys(newErrors)?.length > 0) return false;

    const { matItems, engItems, licItems, outItems } = getComputedItems();
    const calcErrors = validateTotals(calculations, matItems, engItems, licItems, outItems);
    setValidationErrors(calcErrors);
    if (calcErrors?.length > 0) return false;

    return true;
  };

  if (!isOpen) return null;

  const getResultRows = () => {
    const productiveHoursLabel = PRODUCTIVE_HOURS_LABELS?.[pricingBasis] || 'Productive Hours/Month';
    const rows = [
      { section: 'Labor Costs', label: productiveHoursLabel, value: `${calculations?.productiveHoursPerMonth?.toFixed(0)} hrs` },
      { section: 'Labor Costs', label: 'Cost per Productive Hour', value: formatCurrency(calculations?.costPerProductiveHour) },
      { section: 'Labor Costs', label: 'Total Engineer Costs', value: formatCurrency(calculations?.totalEngineerCosts) },
      { section: 'Materials & Licenses', label: 'Materials Cost (with waste)', value: formatCurrency(calculations?.totalMaterialsCost) },
      { section: 'Materials & Licenses', label: 'Licenses Cost', value: formatCurrency(calculations?.totalLicensesCosts) },
      { section: 'Materials & Licenses', label: 'Combined M&L Cost', value: formatCurrency((calculations?.totalMaterialsCost || 0) + (calculations?.totalLicensesCosts || 0)) },
    ];

    if (selectedOutsource?.length > 0) {
      selectedOutsource?.forEach(r => {
        const timeUnitLabel = r?.timeUnit === 'days' ? 'day(s)' : r?.timeUnit === 'months' ? 'month(s)' : 'hr(s)';
        const baseCost = (r?.quantity || 1) * (r?.hours || 0) * (r?.costPerHour || 0);
        const spfCost = r?.spfCost || 0;
        const healthInsurance = r?.healthInsuranceEnabled ? (parseFloat(r?.healthInsuranceValue) || 0) : 0;
        const totalCost = r?.totalCost !== undefined ? r?.totalCost : (baseCost + spfCost + healthInsurance);
        const nationalityLabel = r?.nationality === 'omani' ? 'Omani' : 'Expat';
        rows?.push({
          section: 'Outsource Resources',
          label: `${r?.name || r?.resourceName}${r?.specialization ? ` — ${r?.specialization}` : ''} · ${nationalityLabel} · ${r?.quantity} × ${r?.duration || 1} ${timeUnitLabel} @ ${formatCurrency(r?.costPerHour || 0)}/hr${r?.nationality === 'omani' ? ` + SPF ${formatCurrency(spfCost)}` : ''}${r?.healthInsuranceEnabled ? ` + HI ${formatCurrency(healthInsurance)}` : ''}`,
          value: formatCurrency(totalCost),
        });
      });
      rows?.push({ section: 'Outsource Resources', label: 'Total Outsource Cost', value: formatCurrency(calculations?.totalOutsourceCosts) });
    }

    // Compute summary totals from line items using Monthly Salary + SPF + Health Ins. formula
    const { engItems: _rrEng, matItems: _rrMat, licItems: _rrLic, outItems: _rrOut } = getComputedItems();
    const _rrSt = computeSummaryTotals(_rrEng, _rrMat, _rrLic, _rrOut, calculations);

    rows?.push(
      { section: 'Cost Breakdown', label: 'Direct Costs', value: formatCurrency(_rrSt?.directCosts) },
      { section: 'Cost Breakdown', label: `Overhead (${calculations?.overheadPercentage}%)`, value: formatCurrency(_rrSt?.overheadCosts) },
      { section: 'Cost Breakdown', label: `Contingency (${calculations?.contingencyPercentage}%)`, value: formatCurrency(_rrSt?.contingencyCosts) },
      { section: 'Cost Breakdown', label: 'Total Project Cost', value: formatCurrency(_rrSt?.totalCost) },
      { section: 'Pricing & Profit', label: `Sell Price (${calculations?.marginPercentage}% margin)`, value: formatCurrency(_rrSt?.sellPrice) },
      { section: 'Pricing & Profit', label: 'Gross Profit', value: formatCurrency(_rrSt?.grossProfit) },
      { section: 'Pricing & Profit', label: 'Profit Margin', value: `${_rrSt?.profitMarginPercentage?.toFixed(2)}%` },
    );
    return rows;
  };

  // ── Row builders ──────────────────────────────────────────────────────────

  const getEngineerRows = () => {
    const { engItems } = getComputedItems();
    return engItems?.map(e => ({
      name: e?.name,
      specialization: e?.specialization,
      nationality: e?.nationality,
      count: e?.count,
      hours: e?.hours,
      costPerHour: formatCurrency(e?.costPerHour),
      spf: e?.nationality === 'Omani' ? formatCurrency(e?.spfValue) : '—',
      healthIns: e?.healthInsuranceEnabled ? formatCurrency(e?.healthInsurance) : '—',
      total: formatCurrency(e?.costTotal),
      _costPerHour: e?.costPerHour,
      _costTotal: e?.costTotal,
      _sellCostPerHour: e?.sellCostPerHour,
      _sellTotal: e?.sellTotal,
      _sellPricePerUnitMonth: e?.sellPricePerUnitMonth,
    }));
  };

  const getOutsourceRows = () => {
    const { outItems } = getComputedItems();
    return outItems?.map(o => ({
      name: o?.name,
      vendor: o?.vendor,
      roleSkill: o?.roleLabel,
      nationality: o?.nationality,
      monthlySalary: formatCurrency(o?.monthlySalary),
      duration: `${o?.duration} ${o?.timeUnitLabel}`,
      quantity: o?.qty,
      costPerHour: formatCurrency(o?.costPerHour),
      baseCost: formatCurrency(o?.qty * o?.hours * o?.costPerHour),
      spfCost: o?.isOmani ? formatCurrency(o?.spfCost) : '—',
      healthInsurance: o?.healthInsuranceEnabled ? formatCurrency(o?.healthInsurance) : '—',
      total: formatCurrency(o?.costTotal),
      _costTotal: o?.costTotal,
      _sellCostPerHour: o?.sellCostPerHour,
      _sellTotal: o?.sellTotal,
      _durationLabel: `${o?.duration} ${o?.timeUnitLabel}`,
      _roleLabel: o?.roleLabel,
      _sellPricePerUnitMonth: o?.sellPricePerUnitMonth,
    }));
  };

  const getMaterialRows = () => {
    const { matItems } = getComputedItems();
    return matItems?.map(m => ({
      name: m?.name,
      quantity: m?.qty,
      unitCost: formatCurrency(m?.unitCost),
      waste: `${m?.wastePct}%`,
      total: formatCurrency(m?.costTotal),
      _unitCost: m?.unitCost,
      _costTotal: m?.costTotal,
      _sellUnitCost: m?.sellUnitCost,
      _sellTotal: m?.sellTotal,
      _sellPricePerUnitMonth: m?.sellPricePerUnitMonth,
    }));
  };

  const getLicenseRows = () => {
    const { licItems } = getComputedItems();
    return licItems?.map(l => ({
      name: l?.name,
      vendor: l?.vendor,
      type: l?.type,
      quantity: l?.qty,
      duration: l?.duration,
      unitCost: l?.type === 'Perpetual' ? formatCurrency(l?.unitCostRaw) : `${formatCurrency(l?.unitCostRaw)}/mo`,
      unitCostRaw: l?.unitCostRaw,
      total: formatCurrency(l?.costTotal),
      totalRaw: l?.costTotal,
      _sellUnitCost: l?.sellUnitCost,
      _sellTotal: l?.sellTotal,
      _sellPricePerUnitMonth: l?.sellPricePerUnitMonth,
    }));
  };

  // ── Debug table data ──────────────────────────────────────────────────────
  const getDebugRows = () => {
    const { matItems, engItems, licItems, outItems } = getComputedItems();
    return buildDebugRows(matItems, engItems, licItems, outItems);
  };

  const handleExportPDF = () => {
    return new Promise((resolve, reject) => {
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.onload = () => { try { generatePDF(logoImg); resolve(); } catch(e) { reject(e); } };
      logoImg.onerror = () => { try { generatePDF(null); resolve(); } catch(e) { reject(e); } };
      logoImg.src = `${window.location.origin}/assets/images/logo_PNG_RGB-1776767805644.png`;
    });
  };

  const generatePDF = (logoImg) => {
    const doc = new jsPDF({ orientation: 'landscape', format: 'a4' });
    const pageWidth = doc?.internal?.pageSize?.width;
    const pageHeight = doc?.internal?.pageSize?.height;
    const NAVY = [13, 27, 62];
    const GOLD = [201, 168, 76];
    const GOLD_LIGHT = [245, 230, 184];

    const { engItems: _engI, matItems: _matI, licItems: _licI, outItems: _outI } = getComputedItems();
    const _sumTotals = computeSummaryTotals(_engI, _matI, _licI, _outI, calculations);
    const totalCost = _sumTotals?.totalCost;
    const grossProfit = _sumTotals?.grossProfit;
    const sellPrice = _sumTotals?.sellPrice;
    const profitMargin = _sumTotals?.profitMarginPercentage;
    const directCosts = _sumTotals?.directCosts;
    const overheadCosts = _sumTotals?.overheadCosts;
    const contingencyCosts = _sumTotals?.contingencyCosts;

    const fmtDate = estimateDate
      ? new Date(estimateDate)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : new Date()?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    const addPageFooter = (pageNum, totalPages) => {
      const ph = doc?.internal?.pageSize?.height;
      doc?.setDrawColor(...GOLD);
      doc?.setLineWidth(0.5);
      doc?.line(14, ph - 16, pageWidth - 14, ph - 16);
      doc?.setFontSize(8);
      doc?.setFont('helvetica', 'normal');
      doc?.setTextColor(...NAVY);
      doc?.text(COMPANY_NAME, 14, ph - 9);
      doc?.text(`${estimateNo || ptcNumber || '—'}`, pageWidth / 2, ph - 9, { align: 'center' });
      doc?.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 14, ph - 9, { align: 'right' });
    };

    const addSectionBanner = (text, color, yPos) => {
      doc?.setFillColor(...color);
      doc?.rect(14, yPos, pageWidth - 28, 10, 'F');
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'bold');
      doc?.setTextColor(255, 255, 255);
      doc?.text(text, 18, yPos + 7);
      doc?.setTextColor(0, 0, 0);
      return yPos + 14;
    };

    // ── PAGE 1: EXECUTIVE COVER ───────────────────────────────────────────────
    // Full navy header band
    doc?.setFillColor(...NAVY);
    doc?.rect(0, 0, pageWidth, 75, 'F');

    // Gold accent bar
    doc?.setFillColor(...GOLD);
    doc?.rect(0, 75, pageWidth, 3, 'F');

    // Logo
    if (logoImg) {
      try {
        const maxW = 40;
        const maxH = 40;
        const ratio = Math.min(maxW / logoImg?.naturalWidth, maxH / logoImg?.naturalHeight);
        const logoW = logoImg?.naturalWidth * ratio;
        const logoH = logoImg?.naturalHeight * ratio;
        doc?.addImage(logoImg, 'PNG', 14, 8 + (maxH - logoH) / 2, logoW, logoH);
      } catch (e) {}
    }

    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(18);
    doc?.setFont('helvetica', 'bold');
    doc?.text(COMPANY_NAME, pageWidth / 2, 24, { align: 'center' });

    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(...GOLD_LIGHT);
    doc?.text('INTERNAL ESTIMATE', pageWidth / 2, 34, { align: 'center' });

    doc?.setFontSize(13);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(255, 255, 255);
    doc?.text(estimateTitle || '—', pageWidth / 2, 48, { align: 'center' });

    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(...GOLD_LIGHT);
    doc?.text(`Ref: ${estimateNo || '—'}   ·   PTC: ${ptcNumber || '—'}   ·   ${fmtDate}`, pageWidth / 2, 60, { align: 'center' });

    doc?.setTextColor(0, 0, 0);

    // KPI Summary Cards
    let yPos = 90;
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...NAVY);
    doc?.text('ESTIMATE SUMMARY', 14, yPos);
    yPos += 8;

    const kpiCards = [
      { label: 'Direct Costs', value: formatCurrency(directCosts), color: [41, 128, 185] },
      { label: 'Total Cost', value: formatCurrency(totalCost), color: [52, 73, 94] },
      { label: 'Sell Price', value: formatCurrency(sellPrice), color: [39, 174, 96] },
      { label: 'Gross Profit', value: formatCurrency(grossProfit), color: [142, 68, 173] },
      { label: 'Margin %', value: `${profitMargin?.toFixed(2)}%`, color: [230, 126, 34] },
      { label: 'Overhead %', value: `${calculations?.overheadPercentage || 0}%`, color: [52, 152, 219] },
    ];

    const cardW = (pageWidth - 28 - 10) / 3;
    const cardH = 28;
    const cardGap = 5;

    kpiCards?.forEach((card, idx) => {
      const col = idx % 3;
      const row = Math.floor(idx / 3);
      const cx = 14 + col * (cardW + cardGap);
      const cy = yPos + row * (cardH + cardGap);

      doc?.setFillColor(...card?.color);
      doc?.roundedRect(cx, cy, cardW, cardH, 2, 2, 'F');

      doc?.setFontSize(7.5);
      doc?.setFont('helvetica', 'normal');
      doc?.setTextColor(255, 255, 255);
      doc?.text(card?.label?.toUpperCase(), cx + 5, cy + 9);

      doc?.setFontSize(11);
      doc?.setFont('helvetica', 'bold');
      doc?.text(card?.value, cx + 5, cy + 21);
    });

    yPos += 2 * (cardH + cardGap) + 12;

    // Resource summary table
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...NAVY);
    doc?.text('RESOURCE OVERVIEW', 14, yPos);
    yPos += 6;

    const overviewBody = [];
    if (selectedEngineers?.length > 0) {
      overviewBody?.push(['Engineers', `${selectedEngineers?.length} role(s)`, formatCurrency(_sumTotals?.engCostSum)]);
    }
    if (selectedOutsource?.length > 0) {
      overviewBody?.push(['Outsource Resources', `${selectedOutsource?.length} resource(s)`, formatCurrency(_sumTotals?.outCostSum)]);
    }
    if (selectedMaterials?.length > 0) {
      overviewBody?.push(['Materials', `${selectedMaterials?.length} item(s)`, formatCurrency(_sumTotals?.matCostSum)]);
    }
    if (selectedLicenses?.length > 0) {
      overviewBody?.push(['Licenses', `${selectedLicenses?.length} license(s)`, formatCurrency(_sumTotals?.licCostSum)]);
    }
    overviewBody?.push([
      { content: 'Direct Costs Total', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [230, 240, 255], textColor: NAVY } },
      { content: formatCurrency(directCosts), styles: { fontStyle: 'bold', halign: 'right', fillColor: [230, 240, 255], textColor: NAVY } },
    ]);

    doc?.autoTable({
      startY: yPos,
      head: [['Category', 'Count', 'Cost Total']],
      body: overviewBody,
      theme: 'grid',
      headStyles: { fillColor: NAVY, fontSize: 9, fontStyle: 'bold', textColor: [255, 255, 255] },
      styles: { fontSize: 9 },
      columnStyles: {
        2: { halign: 'right', fontStyle: 'bold' },
      },
    });

    yPos = doc?.lastAutoTable?.finalY + 10;

    // Confidential footer on cover
    doc?.setFontSize(8);
    doc?.setFont('helvetica', 'italic');
    doc?.setTextColor(...NAVY);
    doc?.text('CONFIDENTIAL — INTERNAL USE ONLY', pageWidth / 2, pageHeight - 22, { align: 'center' });

    // ── PAGE 2: ENGINEERS DETAIL ──────────────────────────────────────────────
    doc?.addPage();
    yPos = 20;

    doc?.setFillColor(...NAVY);
    doc?.rect(0, 0, pageWidth, 18, 'F');
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(255, 255, 255);
    doc?.text('ENGINEERS DETAIL', pageWidth / 2, 12, { align: 'center' });
    doc?.setTextColor(0, 0, 0);
    yPos = 26;

    if (selectedEngineers?.length > 0) {
      yPos = addSectionBanner('ENGINEER COST BREAKDOWN', [41, 128, 185], yPos);

      const engRowsDetail = getEngineerRows();
      // Reordered: identifiers | COST group | SELL group
      const engDetailBody = engRowsDetail?.map(e => [
        e?.name,
        e?.specialization,
        e?.nationality,
        // COST group
        formatCurrency(e?._costTotal > 0 ? (e?._costTotal / (e?.count || 1)) : 0)?.replace('OMR ', ''),
        e?.hours,
        e?.count,
        e?.costPerHour,
        e?.spf,
        e?.healthIns,
        e?.total,
        // SELL group
        formatCurrency(e?._sellCostPerHour),
        formatCurrency(e?._sellPricePerUnitMonth),
        formatCurrency(e?._sellTotal),
      ]);
      const engCostTotal = engRowsDetail?.reduce((s, e) => s + e?._costTotal, 0);
      const engSellTotal = engRowsDetail?.reduce((s, e) => s + e?._sellTotal, 0);
      engDetailBody?.push([
        { content: 'TOTAL', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [230, 240, 255], textColor: NAVY } },
        { content: '', styles: { fillColor: [230, 240, 255] } },
        { content: '', styles: { fillColor: [230, 240, 255] } },
        { content: '', styles: { fillColor: [230, 240, 255] } },
        { content: '', styles: { fillColor: [230, 240, 255] } },
        { content: '', styles: { fillColor: [230, 240, 255] } },
        { content: '', styles: { fillColor: [230, 240, 255] } },
        { content: formatCurrency(engCostTotal), styles: { fontStyle: 'bold', halign: 'right', fillColor: [230, 240, 255], textColor: NAVY } },
        { content: '', styles: { fillColor: [230, 240, 255] } },
        { content: '', styles: { fillColor: [230, 240, 255] } },
        { content: formatCurrency(engSellTotal), styles: { fontStyle: 'bold', halign: 'right', fillColor: [230, 240, 255], textColor: [201, 168, 76] } },
      ]);

      // Two-row grouped header
      const engGroupHeader = [
        { content: 'Resource', rowSpan: 2, styles: { fillColor: NAVY, textColor: [255,255,255], fontStyle: 'bold', valign: 'middle', halign: 'center' } },
        { content: 'Role/Skill', rowSpan: 2, styles: { fillColor: NAVY, textColor: [255,255,255], fontStyle: 'bold', valign: 'middle', halign: 'center' } },
        { content: 'Nationality', rowSpan: 2, styles: { fillColor: NAVY, textColor: [255,255,255], fontStyle: 'bold', valign: 'middle', halign: 'center' } },
        { content: 'COST', colSpan: 7, styles: { fillColor: NAVY, textColor: [255,255,255], fontStyle: 'bold', halign: 'center' } },
        { content: 'SELL', colSpan: 3, styles: { fillColor: [201,168,76], textColor: [13,27,62], fontStyle: 'bold', halign: 'center' } },
      ];
      const engSubHeader = [
        { content: 'Monthly Salary', styles: { fillColor: [30,50,100], textColor: [255,255,255], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Duration', styles: { fillColor: [30,50,100], textColor: [255,255,255], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Qty', styles: { fillColor: [30,50,100], textColor: [255,255,255], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Cost/Hr', styles: { fillColor: [30,50,100], textColor: [255,255,255], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'SPF', styles: { fillColor: [30,50,100], textColor: [255,255,255], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Health Ins.', styles: { fillColor: [30,50,100], textColor: [255,255,255], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Cost Total', styles: { fillColor: [30,50,100], textColor: [255,255,255], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Unit Rate', styles: { fillColor: [180,145,50], textColor: [13,27,62], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Sell/Unit/Month', styles: { fillColor: [180,145,50], textColor: [13,27,62], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Line Sell Total', styles: { fillColor: [180,145,50], textColor: [13,27,62], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
      ];

      doc?.autoTable({
        startY: yPos,
        head: [engGroupHeader, engSubHeader],
        body: engDetailBody,
        theme: 'grid',
        headStyles: { fontSize: 8, fontStyle: 'bold' },
        styles: { fontSize: 7.5 },
        columnStyles: {
          9:  { halign: 'right', fontStyle: 'bold' },
          10: { halign: 'right', textColor: [201, 168, 76], fontStyle: 'bold' },
          11: { halign: 'right', textColor: [201, 168, 76], fontStyle: 'bold' },
          12: { halign: 'right', textColor: [201, 168, 76], fontStyle: 'bold' },
        },
      });
      yPos = doc?.lastAutoTable?.finalY + 10;
    } else {
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'italic');
      doc?.setTextColor(120, 120, 120);
      doc?.text('No engineers selected for this estimate.', 14, yPos + 6);
      yPos += 16;
    }

    // ── PAGE 3: OUTSOURCE RESOURCES DETAIL ───────────────────────────────────
    doc?.addPage();
    yPos = 20;

    doc?.setFillColor(...NAVY);
    doc?.rect(0, 0, pageWidth, 18, 'F');
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(255, 255, 255);
    doc?.text('OUTSOURCE RESOURCES DETAIL', pageWidth / 2, 12, { align: 'center' });
    doc?.setTextColor(0, 0, 0);
    yPos = 26;

    if (selectedOutsource?.length > 0) {
      yPos = addSectionBanner('OUTSOURCE RESOURCE COST BREAKDOWN', [16, 185, 129], yPos);

      const outRowsDetail = getOutsourceRows();
      // Reordered: identifiers | COST group | SELL group
      const outsourceBody = outRowsDetail?.map(o => [
        o?.name,
        o?.roleSkill,
        o?.nationality,
        // COST group
        o?.monthlySalary,
        o?.duration,
        o?.quantity,
        o?.costPerHour,
        o?.spfCost,
        o?.healthInsurance,
        o?.total,
        // SELL group
        formatCurrency(o?._sellCostPerHour),
        formatCurrency(o?._sellPricePerUnitMonth),
        formatCurrency(o?._sellTotal),
      ]);
      const outCostTotal = outRowsDetail?.reduce((s, o) => s + o?._costTotal, 0);
      const outSellTotal = outRowsDetail?.reduce((s, o) => s + o?._sellTotal, 0);
      outsourceBody?.push([
        { content: 'TOTAL', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [230, 245, 240], textColor: [16, 185, 129] } },
        { content: '', styles: { fillColor: [230, 245, 240] } },
        { content: '', styles: { fillColor: [230, 245, 240] } },
        { content: '', styles: { fillColor: [230, 245, 240] } },
        { content: '', styles: { fillColor: [230, 245, 240] } },
        { content: '', styles: { fillColor: [230, 245, 240] } },
        { content: '', styles: { fillColor: [230, 245, 240] } },
        { content: formatCurrency(outCostTotal), styles: { fontStyle: 'bold', halign: 'right', fillColor: [230, 245, 240], textColor: [16, 185, 129] } },
        { content: '', styles: { fillColor: [230, 245, 240] } },
        { content: '', styles: { fillColor: [230, 245, 240] } },
        { content: formatCurrency(outSellTotal), styles: { fontStyle: 'bold', halign: 'right', fillColor: [230, 245, 240], textColor: [201, 168, 76] } },
      ]);

      // Two-row grouped header
      const outGroupHeader = [
        { content: 'Resource', rowSpan: 2, styles: { fillColor: NAVY, textColor: [255,255,255], fontStyle: 'bold', valign: 'middle', halign: 'center' } },
        { content: 'Role/Skill', rowSpan: 2, styles: { fillColor: NAVY, textColor: [255,255,255], fontStyle: 'bold', valign: 'middle', halign: 'center' } },
        { content: 'Nationality', rowSpan: 2, styles: { fillColor: NAVY, textColor: [255,255,255], fontStyle: 'bold', valign: 'middle', halign: 'center' } },
        { content: 'COST', colSpan: 7, styles: { fillColor: NAVY, textColor: [255,255,255], fontStyle: 'bold', halign: 'center' } },
        { content: 'SELL', colSpan: 3, styles: { fillColor: [201,168,76], textColor: [13,27,62], fontStyle: 'bold', halign: 'center' } },
      ];
      const outSubHeader = [
        { content: 'Monthly Salary', styles: { fillColor: [30,50,100], textColor: [255,255,255], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Duration', styles: { fillColor: [30,50,100], textColor: [255,255,255], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Qty', styles: { fillColor: [30,50,100], textColor: [255,255,255], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Cost/Hr', styles: { fillColor: [30,50,100], textColor: [255,255,255], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'SPF', styles: { fillColor: [30,50,100], textColor: [255,255,255], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Health Ins.', styles: { fillColor: [30,50,100], textColor: [255,255,255], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Cost Total', styles: { fillColor: [30,50,100], textColor: [255,255,255], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Unit Rate', styles: { fillColor: [180,145,50], textColor: [13,27,62], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Sell/Unit/Month', styles: { fillColor: [180,145,50], textColor: [13,27,62], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
        { content: 'Line Sell Total', styles: { fillColor: [180,145,50], textColor: [13,27,62], fontStyle: 'bold', halign: 'center', fontSize: 7 } },
      ];

      doc?.autoTable({
        startY: yPos,
        head: [outGroupHeader, outSubHeader],
        body: outsourceBody,
        theme: 'grid',
        headStyles: { fontSize: 8, fontStyle: 'bold' },
        styles: { fontSize: 7.5 },
        columnStyles: {
          9:  { halign: 'right', fontStyle: 'bold' },
          10: { halign: 'right', textColor: [201, 168, 76], fontStyle: 'bold' },
          11: { halign: 'right', textColor: [201, 168, 76], fontStyle: 'bold' },
          12: { halign: 'right', textColor: [201, 168, 76], fontStyle: 'bold' },
        },
      });
      yPos = doc?.lastAutoTable?.finalY + 10;
    } else {
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'italic');
      doc?.setTextColor(120, 120, 120);
      doc?.text('No outsource resources selected for this estimate.', 14, yPos + 6);
      yPos += 16;
    }

    // ── PAGE 4: MATERIALS & LICENSES ─────────────────────────────────────────
    doc?.addPage();
    yPos = 20;

    doc?.setFillColor(...NAVY);
    doc?.rect(0, 0, pageWidth, 18, 'F');
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(255, 255, 255);
    doc?.text('MATERIALS & LICENSES', pageWidth / 2, 12, { align: 'center' });
    doc?.setTextColor(0, 0, 0);
    yPos = 26;

    if (selectedMaterials?.length > 0) {
      yPos = addSectionBanner('MATERIALS COST BREAKDOWN', [180, 83, 9], yPos);

      const matRowsDetail = getMaterialRows();
      const matDetailBody = matRowsDetail?.map(m => [
        m?.name,
        m?.quantity,
        m?.unitCost,
        formatCurrency(m?._sellUnitCost),
        formatCurrency(m?._sellPricePerUnitMonth),
        m?.waste,
        m?.total,
      ]);
      const matCostTotal = matRowsDetail?.reduce((s, m) => s + m?._costTotal, 0);
      matDetailBody?.push([
        { content: 'Total Materials Cost', colSpan: 6, styles: { fontStyle: 'bold', fillColor: [255, 243, 230], textColor: [180, 83, 9] } },
        { content: formatCurrency(matCostTotal), styles: { fontStyle: 'bold', halign: 'right', fillColor: [255, 243, 230], textColor: [180, 83, 9] } },
      ]);
      doc?.autoTable({
        startY: yPos,
        head: [['Material', 'Qty', 'Unit Cost (DB)', 'Unit Rate', 'Sell/Unit/Mo', 'Waste', 'Total']],
        body: matDetailBody,
        theme: 'grid',
        headStyles: { fillColor: [180, 83, 9], fontSize: 9, fontStyle: 'bold' },
        styles: { fontSize: 8.5 },
        columnStyles: {
          3: { halign: 'right', textColor: [201, 168, 76], fontStyle: 'bold' },
          4: { halign: 'right', textColor: [201, 168, 76], fontStyle: 'bold' },
          6: { halign: 'right', fontStyle: 'bold' },
        },
      });
      yPos = doc?.lastAutoTable?.finalY + 12;
    }

    if (selectedLicenses?.length > 0) {
      if (yPos > 220) { doc?.addPage(); yPos = 20; }
      yPos = addSectionBanner('LICENSES COST BREAKDOWN', [15, 118, 110], yPos);

      const licRowsDetail = getLicenseRows();
      const licDetailBody = licRowsDetail?.map(l => [
        l?.name,
        l?.vendor,
        l?.type,
        l?.quantity,
        l?.duration,
        l?.unitCost,
        formatCurrency(l?._sellUnitCost),
        formatCurrency(l?._sellPricePerUnitMonth),
        l?.total,
      ]);
      const licCostTotal = licRowsDetail?.reduce((s, l) => s + l?.totalRaw, 0);
      licDetailBody?.push([
        { content: 'Total Licenses Cost', colSpan: 8, styles: { fontStyle: 'bold', fillColor: [230, 255, 253], textColor: [15, 118, 110] } },
        { content: formatCurrency(licCostTotal), styles: { fontStyle: 'bold', halign: 'right', fillColor: [230, 255, 253], textColor: [15, 118, 110] } },
      ]);
      doc?.autoTable({
        startY: yPos,
        head: [['License', 'Vendor/Provider', 'Type', 'Count', 'Duration (mo)', 'Unit Cost', 'Unit Rate', 'Sell/Unit/Mo', 'Total']],
        body: licDetailBody,
        theme: 'grid',
        headStyles: { fillColor: [15, 118, 110], fontSize: 8, fontStyle: 'bold' },
        styles: { fontSize: 8 },
        columnStyles: {
          6: { halign: 'right', textColor: [201, 168, 76], fontStyle: 'bold' },
          7: { halign: 'right', textColor: [201, 168, 76], fontStyle: 'bold' },
          8: { halign: 'right', fontStyle: 'bold' },
        },
      });
      yPos = doc?.lastAutoTable?.finalY + 10;
    }

    if (!selectedMaterials?.length && !selectedLicenses?.length) {
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'italic');
      doc?.setTextColor(120, 120, 120);
      doc?.text('No materials or licenses selected for this estimate.', 14, yPos + 6);
    }

    // ── PAGE 5: FIXED EXPENSES ────────────────────────────────────────────────
    doc?.addPage();
    yPos = 20;

    doc?.setFillColor(...NAVY);
    doc?.rect(0, 0, pageWidth, 18, 'F');
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(255, 255, 255);
    doc?.text('FIXED EXPENSES', pageWidth / 2, 12, { align: 'center' });
    doc?.setTextColor(0, 0, 0);
    yPos = 26;

    yPos = addSectionBanner('OVERHEAD & CONTINGENCY BREAKDOWN', [52, 73, 94], yPos);

    const overheadPct = calculations?.overheadPercentage || 0;
    const contingencyPct = calculations?.contingencyPercentage || 0;
    const marginPct = calculations?.marginPercentage || 0;

    doc?.autoTable({
      startY: yPos,
      head: [['Expense Category', 'Base Amount', 'Rate (%)', 'Amount (OMR)', 'Notes']],
      body: [
        [
          'Overhead',
          formatCurrency(directCosts),
          `${overheadPct}%`,
          formatCurrency(overheadCosts),
          'Applied on Direct Costs',
        ],
        [
          'Contingency',
          formatCurrency(directCosts + overheadCosts),
          `${contingencyPct}%`,
          formatCurrency(contingencyCosts),
          'Applied on Direct Costs + Overhead',
        ],
        [
          { content: 'Total Fixed Expenses', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [230, 240, 255], textColor: NAVY } },
          { content: formatCurrency(overheadCosts + contingencyCosts), styles: { fontStyle: 'bold', halign: 'right', fillColor: [230, 240, 255], textColor: NAVY } },
          { content: '', styles: { fillColor: [230, 240, 255] } },
        ],
      ],
      theme: 'grid',
      headStyles: { fillColor: [52, 73, 94], fontSize: 9, fontStyle: 'bold' },
      styles: { fontSize: 9 },
      columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'center', fontStyle: 'bold' },
        3: { halign: 'right', fontStyle: 'bold' },
        4: { halign: 'left', textColor: [100, 100, 100] },
      },
    });
    yPos = doc?.lastAutoTable?.finalY + 12;

    yPos = addSectionBanner('COST COMPOSITION', [52, 73, 94], yPos);

    doc?.autoTable({
      startY: yPos,
      head: [['Cost Component', 'Amount (OMR)', '% of Total Cost']],
      body: [
        ['Engineer Costs', formatCurrency(_sumTotals?.engCostSum), totalCost > 0 ? `${((_sumTotals?.engCostSum / totalCost) * 100)?.toFixed(1)}%` : '—'],
        ['Outsource Costs', formatCurrency(_sumTotals?.outCostSum), totalCost > 0 ? `${((_sumTotals?.outCostSum / totalCost) * 100)?.toFixed(1)}%` : '—'],
        ['Materials Cost', formatCurrency(_sumTotals?.matCostSum), totalCost > 0 ? `${((_sumTotals?.matCostSum / totalCost) * 100)?.toFixed(1)}%` : '—'],
        ['Licenses Cost', formatCurrency(_sumTotals?.licCostSum), totalCost > 0 ? `${((_sumTotals?.licCostSum / totalCost) * 100)?.toFixed(1)}%` : '—'],
        [
          { content: 'Direct Costs Subtotal', styles: { fontStyle: 'bold', fillColor: [240, 244, 248], textColor: [52, 73, 94] } },
          { content: formatCurrency(directCosts), styles: { fontStyle: 'bold', halign: 'right', fillColor: [240, 244, 248], textColor: [52, 73, 94] } },
          { content: totalCost > 0 ? `${((directCosts / totalCost) * 100)?.toFixed(1)}%` : '—', styles: { halign: 'right', fillColor: [240, 244, 248] } },
        ],
        ['Overhead', formatCurrency(overheadCosts), totalCost > 0 ? `${((overheadCosts / totalCost) * 100)?.toFixed(1)}%` : '—'],
        ['Contingency', formatCurrency(contingencyCosts), totalCost > 0 ? `${((contingencyCosts / totalCost) * 100)?.toFixed(1)}%` : '—'],
        [
          { content: 'TOTAL COST', styles: { fontStyle: 'bold', fillColor: [230, 240, 255], textColor: NAVY } },
          { content: formatCurrency(totalCost), styles: { fontStyle: 'bold', halign: 'right', fillColor: [230, 240, 255], textColor: NAVY } },
          { content: '100.0%', styles: { fontStyle: 'bold', halign: 'right', fillColor: [230, 240, 255], textColor: NAVY } },
        ],
      ],
      theme: 'grid',
      headStyles: { fillColor: [52, 73, 94], fontSize: 9, fontStyle: 'bold' },
      styles: { fontSize: 9 },
      columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right' },
      },
    });
    yPos = doc?.lastAutoTable?.finalY + 10;

    // ── PAGE 6: FINANCIAL SUMMARY ─────────────────────────────────────────────
    doc?.addPage();
    yPos = 20;

    doc?.setFillColor(...NAVY);
    doc?.rect(0, 0, pageWidth, 18, 'F');
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(255, 255, 255);
    doc?.text('FINANCIAL SUMMARY', pageWidth / 2, 12, { align: 'center' });
    doc?.setTextColor(0, 0, 0);
    yPos = 26;

    // Pie chart
    const canvas = document.createElement('canvas');
    const chartSize = 220;
    canvas.width = chartSize;
    canvas.height = chartSize;
    const ctx = canvas?.getContext('2d');
    const cx = chartSize / 2;
    const cy = chartSize / 2;
    const radius = chartSize / 2 - 10;
    const slices = [
      { value: totalCost, color: '#8b5cf6', label: 'Total Cost' },
      { value: grossProfit, color: '#06b6d4', label: 'Gross Profit' },
    ];
    const total = slices?.reduce((sum, s) => sum + s?.value, 0);
    let startAngle = -Math.PI / 2;
    slices?.forEach(slice => {
      const sliceAngle = total > 0 ? (slice?.value / total) * 2 * Math.PI : Math.PI;
      ctx?.beginPath();
      ctx?.moveTo(cx, cy);
      ctx?.arc(cx, cy, radius, startAngle, startAngle + sliceAngle);
      ctx?.closePath();
      ctx.fillStyle = slice?.color;
      ctx?.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx?.stroke();
      if (total > 0 && slice?.value > 0) {
        const midAngle = startAngle + sliceAngle / 2;
        const labelRadius = radius * 0.62;
        const lx = cx + labelRadius * Math.cos(midAngle);
        const ly = cy + labelRadius * Math.sin(midAngle);
        const pct = ((slice?.value / total) * 100)?.toFixed(1) + '%';
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx?.fillText(pct, lx, ly);
      }
      startAngle += sliceAngle;
    });

    const chartImgData = canvas?.toDataURL('image/png');
    const chartPdfWidth = 70;
    const chartPdfHeight = 70;
    const chartX = 14;
    const chartY = yPos;
    doc?.addImage(chartImgData, 'PNG', chartX, chartY, chartPdfWidth, chartPdfHeight);

    const legendX = chartX + chartPdfWidth + 10;
    let legendY = chartY + 8;
    slices?.forEach(slice => {
      const pct = total > 0 ? ((slice?.value / total) * 100)?.toFixed(1) : '0.0';
      const rgb = slice?.color === '#8b5cf6' ? [139, 92, 246] : [6, 182, 212];
      doc?.setFillColor(...rgb);
      doc?.roundedRect(legendX, legendY - 3, 8, 8, 1, 1, 'F');
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'bold');
      doc?.setTextColor(50, 50, 50);
      doc?.text(slice?.label, legendX + 11, legendY + 2);
      doc?.setFont('helvetica', 'normal');
      doc?.setFontSize(8);
      doc?.setTextColor(80, 80, 80);
      doc?.text(`${formatCurrency(slice?.value)}  (${pct}%)`, legendX + 11, legendY + 9);
      legendY += 22;
    });

    legendY += 4;
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(50, 50, 50);
    doc?.text('Selling Price:', legendX, legendY);
    doc?.setFont('helvetica', 'normal');
    doc?.text(formatCurrency(sellPrice), legendX + 30, legendY);
    legendY += 8;
    doc?.setFont('helvetica', 'bold');
    doc?.text('Profit Margin:', legendX, legendY);
    doc?.setFont('helvetica', 'normal');
    doc?.text(`${profitMargin?.toFixed(2)}%`, legendX + 30, legendY);

    yPos = chartY + chartPdfHeight + 12;

    yPos = addSectionBanner('FINANCIAL SUMMARY', NAVY, yPos);

    const totalPct = sellPrice > 0 ? (totalCost / sellPrice) : 0;
    const profitPct = sellPrice > 0 ? (grossProfit / sellPrice) : 0;

    doc?.autoTable({
      startY: yPos,
      head: [['Financial Metric', 'Amount (OMR)', '% of Selling Price', 'Notes']],
      body: [
        ['Direct Costs', formatCurrency(directCosts), `${(directCosts / (sellPrice || 1) * 100)?.toFixed(1)}%`, 'Engineers + Outsource + Materials + Licenses'],
        [`Overhead (${overheadPct}%)`, formatCurrency(overheadCosts), `${(overheadCosts / (sellPrice || 1) * 100)?.toFixed(1)}%`, `${overheadPct}% of Direct Costs`],
        [`Contingency (${contingencyPct}%)`, formatCurrency(contingencyCosts), `${(contingencyCosts / (sellPrice || 1) * 100)?.toFixed(1)}%`, `${contingencyPct}% of (Direct + Overhead)`],
        [
          { content: 'Total Cost', styles: { fontStyle: 'bold', fillColor: [230, 240, 255], textColor: NAVY } },
          { content: formatCurrency(totalCost), styles: { fontStyle: 'bold', halign: 'right', fillColor: [230, 240, 255], textColor: NAVY } },
          { content: `${(totalPct * 100)?.toFixed(1)}%`, styles: { halign: 'right', fillColor: [230, 240, 255] } },
          { content: 'Direct + Overhead + Contingency', styles: { fillColor: [230, 240, 255], textColor: [80, 80, 80] } },
        ],
        [
          { content: `Gross Profit (Margin ${marginPct}%)`, styles: { fontStyle: 'bold', fillColor: [230, 255, 240], textColor: [39, 174, 96] } },
          { content: formatCurrency(grossProfit), styles: { fontStyle: 'bold', halign: 'right', fillColor: [230, 255, 240], textColor: [39, 174, 96] } },
          { content: `${(profitPct * 100)?.toFixed(1)}%`, styles: { halign: 'right', fillColor: [230, 255, 240] } },
          { content: 'Sell Price − Total Cost', styles: { fillColor: [230, 255, 240], textColor: [80, 80, 80] } },
        ],
        [
          { content: 'SELL PRICE', styles: { fontStyle: 'bold', fillColor: NAVY, textColor: [255, 255, 255] } },
          { content: formatCurrency(sellPrice), styles: { fontStyle: 'bold', halign: 'right', fillColor: NAVY, textColor: [255, 255, 255] } },
          { content: '100.0%', styles: { fontStyle: 'bold', halign: 'right', fillColor: NAVY, textColor: [255, 255, 255] } },
          { content: `Margin: ${profitMargin?.toFixed(2)}%`, styles: { fillColor: NAVY, textColor: GOLD_LIGHT } },
        ],
      ],
      theme: 'grid',
      headStyles: { fillColor: NAVY, fontSize: 9, fontStyle: 'bold' },
      styles: { fontSize: 9 },
      columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'left', textColor: [100, 100, 100] },
      },
    });

    // Add footers to all pages
    const totalPages = doc?.internal?.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc?.setPage(i);
      addPageFooter(i, totalPages);
    }

    doc?.save(`Internal_${(estimateTitle || 'Estimate')?.replace(/\s+/g, '_')}_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`);
    setIsExporting(false);
  };

  // ── Customer PDF (3 pages: Cover, Scope of Services, Quotation Summary) ──────
  const handleExportCustomerPDF = () => {
    return new Promise((resolve, reject) => {
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.onload = () => { try { generateCustomerPDF(logoImg); resolve(); } catch(e) { reject(e); } };
      logoImg.onerror = () => { try { generateCustomerPDF(null); resolve(); } catch(e) { reject(e); } };
      logoImg.src = `${window.location.origin}/assets/images/logo_PNG_RGB-1776767805644.png`;
    });
  };

  const generateCustomerPDF = (logoImg) => {
    const doc = new jsPDF();
    const pageWidth = doc?.internal?.pageSize?.width;
    const pageHeight = doc?.internal?.pageSize?.height;
    const NAVY = [13, 27, 62];
    const GOLD = [201, 168, 76];
    const GOLD_LIGHT = [245, 230, 184];
    const WHITE = [255, 255, 255];
    const LIGHT_GREY = [244, 246, 251];

    // Compute sell price from line items using Monthly Salary + SPF + Health Ins. formula
    const { engItems: _cpEng, matItems: _cpMat, licItems: _cpLic, outItems: _cpOut } = getComputedItems();
    const _cpSt = computeSummaryTotals(_cpEng, _cpMat, _cpLic, _cpOut, calculations);
    const computedSellPrice = _cpSt?.sellPrice;

    const fmtDate = estimateDate
      ? new Date(estimateDate)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : '—';

    const addFooter = (pageNum, totalPages) => {
      doc?.setDrawColor(...GOLD);
      doc?.setLineWidth(0.5);
      doc?.line(14, pageHeight - 18, pageWidth - 14, pageHeight - 18);
      doc?.setFontSize(8);
      doc?.setTextColor(120, 120, 120);
      doc?.text(COMPANY_NAME, 14, pageHeight - 10);
      doc?.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 14, pageHeight - 10, { align: 'right' });
    };

    // ── PAGE 1: QUOTATION COVER ───────────────────────────────────────────────
    // Full navy header band
    doc?.setFillColor(...NAVY);
    doc?.rect(0, 0, pageWidth, 70, 'F');

    // Gold accent bar at bottom of header
    doc?.setFillColor(...GOLD);
    doc?.rect(0, 68, pageWidth, 3, 'F');

    // Logo
    if (logoImg) {
      try {
        const maxW = 40, maxH = 40;
        const ratio = Math.min(maxW / logoImg?.naturalWidth, maxH / logoImg?.naturalHeight);
        const lw = logoImg?.naturalWidth * ratio;
        const lh = logoImg?.naturalHeight * ratio;
        doc?.addImage(logoImg, 'PNG', 14, 8 + (maxH - lh) / 2, lw, lh);
      } catch(e) {}
    }

    // Company name
    doc?.setTextColor(...WHITE);
    doc?.setFontSize(18);
    doc?.setFont('helvetica', 'bold');
    doc?.text(COMPANY_NAME, 60, 22);

    // Subtitle
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(...GOLD_LIGHT);
    doc?.text('IT Services & Solutions Provider', 60, 32);

    // "SERVICE QUOTATION" title block
    doc?.setFillColor(...GOLD);
    doc?.rect(14, 82, pageWidth - 28, 14, 'F');
    doc?.setTextColor(...NAVY);
    doc?.setFontSize(14);
    doc?.setFont('helvetica', 'bold');
    doc?.text('SERVICE QUOTATION', pageWidth / 2, 92, { align: 'center' });

    // Intro text
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'italic');
    doc?.setTextColor(80, 80, 80);
    const introText = 'We are pleased to present the following service quotation for your review and approval.';
    const introLines = doc?.splitTextToSize(introText, pageWidth - 28);
    doc?.text(introLines, 14, 108);

    // Quote details box
    let yPos = 122;
    doc?.setFillColor(...LIGHT_GREY);
    doc?.roundedRect(14, yPos, pageWidth - 28, 52, 3, 3, 'F');
    doc?.setDrawColor(...NAVY);
    doc?.setLineWidth(0.5);
    doc?.roundedRect(14, yPos, pageWidth - 28, 52, 3, 3, 'S');

    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...NAVY);
    const leftX = 22, rightX = pageWidth / 2 + 6;
    const lineH = 10;
    yPos += 10;

    const detailRows = [
      ['Quote Reference:', estimateNo || '—', 'Date:', fmtDate],
      ['PTC Reference:', ptcNumber || '—', 'Validity:', '30 Days from Date'],
      ['Project:', estimateTitle || '—', 'Delivery Mode:', (deliveryMode || 'Standard')?.charAt(0)?.toUpperCase() + (deliveryMode || 'Standard')?.slice(1)],
    ];

    detailRows?.forEach(([lLabel, lVal, rLabel, rVal]) => {
      doc?.setFont('helvetica', 'bold');
      doc?.setTextColor(...NAVY);
      doc?.text(lLabel, leftX, yPos);
      doc?.setFont('helvetica', 'normal');
      doc?.setTextColor(60, 60, 60);
      doc?.text(lVal, leftX + 38, yPos);
      doc?.setFont('helvetica', 'bold');
      doc?.setTextColor(...NAVY);
      doc?.text(rLabel, rightX, yPos);
      doc?.setFont('helvetica', 'normal');
      doc?.setTextColor(60, 60, 60);
      doc?.text(rVal, rightX + 32, yPos);
      yPos += lineH;
    });

    // Grand total highlight
    yPos = 190;
    doc?.setFillColor(...NAVY);
    doc?.roundedRect(14, yPos, pageWidth - 28, 22, 3, 3, 'F');
    doc?.setTextColor(...GOLD_LIGHT);
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.text('TOTAL QUOTED VALUE', 22, yPos + 9);
    doc?.setFontSize(14);
    doc?.setTextColor(...GOLD);
        doc?.text(formatCurrency(computedSellPrice), pageWidth - 22, yPos + 13, { align: 'right' });

    // Terms note
    yPos = 224;
    doc?.setFontSize(8.5);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(100, 100, 100);
    const termsText = 'All prices are in Omani Rial (OMR) and are exclusive of applicable taxes unless stated otherwise. This quotation is valid for 30 days from the date of issue.';
    const termsLines = doc?.splitTextToSize(termsText, pageWidth - 28);
    doc?.text(termsLines, 14, yPos);

    // Signature block
    yPos = 248;
    doc?.setDrawColor(200, 200, 200);
    doc?.line(14, yPos, 80, yPos);
    doc?.line(pageWidth - 80, yPos, pageWidth - 14, yPos);
    doc?.setFontSize(8);
    doc?.setTextColor(120, 120, 120);
    doc?.text('Authorized Signature', 14, yPos + 6);
    doc?.text('Client Acceptance', pageWidth - 14, yPos + 6, { align: 'right' });

    // ── PAGE 2: SCOPE OF SERVICES ─────────────────────────────────────────────
    doc?.addPage();

    // Page header band
    doc?.setFillColor(...NAVY);
    doc?.rect(0, 0, pageWidth, 28, 'F');
    doc?.setFillColor(...GOLD);
    doc?.rect(0, 26, pageWidth, 2.5, 'F');
    doc?.setTextColor(...WHITE);
    doc?.setFontSize(14);
    doc?.setFont('helvetica', 'bold');
    doc?.text('SCOPE OF SERVICES', 14, 18);
    doc?.setFontSize(8);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(...GOLD_LIGHT);
    doc?.text(`${estimateTitle || '—'}  ·  Ref: ${ptcNumber || '—'}  ·  ${fmtDate}`, pageWidth - 14, 18, { align: 'right' });

    yPos = 40;

    // ── Engineers section (Customer PDF) ──────────────────────────────────
    if (selectedEngineers?.length > 0) {
      if (yPos > 220) { doc?.addPage(); yPos = 20; }

      // Section banner
      doc?.setFillColor(...NAVY);
      doc?.rect(14, yPos, pageWidth - 28, 10, 'F');
      doc?.setTextColor(...GOLD_LIGHT);
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'bold');
      doc?.text('▶  PROFESSIONAL ENGINEERS', 18, yPos + 7);
      yPos += 14;

      const { engItems } = getComputedItems();
      const engBody = engItems?.map(e => {
        return [
          e?.name,
          e?.specialization || '—',
          `${e?.count}`,
          `${e?.hours} hrs`,
          formatCurrency(e?.sellPricePerUnitMonth),
          formatCurrency(e?.sellTotal),
        ];
      });
      let totalEngSell = engItems?.reduce((s, e) => s + e?.sellTotal, 0);
      engBody?.push([
        { content: 'Subtotal — Engineers', colSpan: 5, styles: { fontStyle: 'bold', fillColor: NAVY, textColor: WHITE } },
        { content: formatCurrency(totalEngSell), styles: { fontStyle: 'bold', halign: 'right', fillColor: NAVY, textColor: [201, 168, 76] } },
      ]);

      doc?.autoTable({
        startY: yPos,
        head: [['Engineer / Role', 'Specialization', 'Count', 'Duration', 'Sell/Unit/Month', 'Line Total']],
        body: engBody,
        theme: 'grid',
        headStyles: { fillColor: [26, 47, 94], fontSize: 8.5, fontStyle: 'bold', textColor: WHITE },
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 36 },
          1: { cellWidth: 32 },
          2: { cellWidth: 12, halign: 'center' },
          3: { cellWidth: 16, halign: 'center' },
          4: { cellWidth: 28, halign: 'right', textColor: [201, 168, 76], fontStyle: 'bold' },
          5: { cellWidth: 26, halign: 'right', fontStyle: 'bold' },
        },
        alternateRowStyles: { fillColor: LIGHT_GREY },
      });
      yPos = doc?.lastAutoTable?.finalY + 8;
    }

    // ── Materials section ─────────────────────────────────────────────────────
    if (selectedMaterials?.length > 0) {
      if (yPos > 220) { doc?.addPage(); yPos = 20; }

      doc?.setFillColor(180, 83, 9);
      doc?.rect(14, yPos, pageWidth - 28, 10, 'F');
      doc?.setTextColor(...WHITE);
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'bold');
      doc?.text('▶  MATERIALS & EQUIPMENT', 18, yPos + 7);
      yPos += 14;

      const { matItems } = getComputedItems();
      const matBody = matItems?.map(m => [
        m?.name,
        `${m?.qty}`,
        formatCurrency(m?.sellPricePerUnitMonth),
        formatCurrency(m?.sellTotal),
      ]);
      let totalMatSell = matItems?.reduce((s, m) => s + m?.sellTotal, 0);
      matBody?.push([
        { content: 'Subtotal — Materials', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [180, 83, 9], textColor: WHITE } },
        { content: formatCurrency(totalMatSell), styles: { fontStyle: 'bold', halign: 'right', fillColor: [180, 83, 9], textColor: WHITE } },
      ]);

      doc?.autoTable({
        startY: yPos,
        head: [['Material / Item', 'Qty', 'Sell/Unit/Month', 'Line Total']],
        body: matBody,
        theme: 'grid',
        headStyles: { fillColor: [217, 119, 6], fontSize: 8.5, fontStyle: 'bold', textColor: WHITE },
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: 18, halign: 'center' },
          2: { cellWidth: 36, halign: 'right', textColor: [201, 168, 76], fontStyle: 'bold' },
          3: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
        },
        alternateRowStyles: { fillColor: [255, 251, 235] },
      });
      yPos = doc?.lastAutoTable?.finalY + 8;
    }

    // ── Licenses section ──────────────────────────────────────────────────────
    if (selectedLicenses?.length > 0) {
      if (yPos > 220) { doc?.addPage(); yPos = 20; }

      doc?.setFillColor(15, 118, 110);
      doc?.rect(14, yPos, pageWidth - 28, 10, 'F');
      doc?.setTextColor(...WHITE);
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'bold');
      doc?.text('▶  SOFTWARE LICENSES', 18, yPos + 7);
      yPos += 14;

      const { licItems } = getComputedItems();
      const licBody = licItems?.map(l => {
        const countDur = l?.type === 'Perpetual' ? `${l?.qty} unit(s)` : `${l?.qty} × ${l?.duration} mo`;
        return [
          l?.name,
          l?.vendor || '—',
          l?.type,
          countDur,
          formatCurrency(l?.sellPricePerUnitMonth),
          formatCurrency(l?.sellTotal),
        ];
      });
      let totalLicSell = licItems?.reduce((s, l) => s + l?.sellTotal, 0);
      licBody?.push([
        { content: 'Subtotal — Licenses', colSpan: 5, styles: { fontStyle: 'bold', fillColor: [15, 118, 110], textColor: WHITE } },
        { content: formatCurrency(totalLicSell), styles: { fontStyle: 'bold', halign: 'right', fillColor: [15, 118, 110], textColor: WHITE } },
      ]);

      doc?.autoTable({
        startY: yPos,
        head: [['License / Software', 'Vendor', 'Type', 'Qty / Duration', 'Sell/Unit/Month', 'Line Total']],
        body: licBody,
        theme: 'grid',
        headStyles: { fillColor: [13, 148, 136], fontSize: 8.5, fontStyle: 'bold', textColor: WHITE },
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 38 },
          1: { cellWidth: 26 },
          2: { cellWidth: 20 },
          3: { cellWidth: 22, halign: 'center' },
          4: { cellWidth: 30, halign: 'right', textColor: [201, 168, 76], fontStyle: 'bold' },
          5: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
        },
        alternateRowStyles: { fillColor: [204, 251, 241] },
      });
      yPos = doc?.lastAutoTable?.finalY + 8;
    }

    // ── Outsource section ─────────────────────────────────────────────────────
    if (selectedOutsource?.length > 0) {
      if (yPos > 220) { doc?.addPage(); yPos = 20; }

      doc?.setFillColor(109, 40, 217);
      doc?.rect(14, yPos, pageWidth - 28, 10, 'F');
      doc?.setTextColor(...WHITE);
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'bold');
      doc?.text('▶  OUTSOURCE RESOURCES', 18, yPos + 7);
      yPos += 14;

      const { outItems } = getComputedItems();
      const outBody = outItems?.map(o => {
        return [
          o?.name,
          o?.roleLabel || '—',
          `${o?.qty}`,
          `${o?.duration} ${o?.timeUnitLabel}`,
          formatCurrency(o?.sellPricePerUnitMonth),
          formatCurrency(o?.sellTotal),
        ];
      });
      let totalOutSell = outItems?.reduce((s, o) => s + o?.sellTotal, 0);
      outBody?.push([
        { content: 'Subtotal — Outsource', colSpan: 5, styles: { fontStyle: 'bold', fillColor: [109, 40, 217], textColor: WHITE } },
        { content: formatCurrency(totalOutSell), styles: { fontStyle: 'bold', halign: 'right', fillColor: [109, 40, 217], textColor: WHITE } },
      ]);

      doc?.autoTable({
        startY: yPos,
        head: [['Resource', 'Role / Skill', 'Qty', 'Duration', 'Sell/Unit/Month', 'Line Total']],
        body: outBody,
        theme: 'grid',
        headStyles: { fillColor: [124, 58, 237], fontSize: 8.5, fontStyle: 'bold', textColor: WHITE },
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 36 },
          1: { cellWidth: 32 },
          2: { cellWidth: 12, halign: 'center' },
          3: { cellWidth: 20, halign: 'center' },
          4: { cellWidth: 30, halign: 'right', textColor: [201, 168, 76], fontStyle: 'bold' },
          5: { cellWidth: 26, halign: 'right', fontStyle: 'bold' },
        },
        alternateRowStyles: { fillColor: [237, 233, 254] },
      });
      yPos = doc?.lastAutoTable?.finalY + 8;
    }

    // ── PAGE 3: QUOTATION SUMMARY ─────────────────────────────────────────────
    doc?.addPage();

    doc?.setFillColor(...NAVY);
    doc?.rect(0, 0, pageWidth, 28, 'F');
    doc?.setFillColor(...GOLD);
    doc?.rect(0, 26, pageWidth, 2.5, 'F');
    doc?.setTextColor(...WHITE);
    doc?.setFontSize(14);
    doc?.setFont('helvetica', 'bold');
    doc?.text('QUOTATION SUMMARY', 14, 18);
    doc?.setFontSize(8);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(...GOLD_LIGHT);
    doc?.text(`${estimateTitle || '—'}  ·  Ref: ${ptcNumber || '—'}  ·  ${fmtDate}`, pageWidth - 14, 18, { align: 'right' });

    yPos = 40;

    // Section subtotals table
    const { engItems, matItems, licItems, outItems } = getComputedItems();
    const summaryRows = [];

    if (selectedEngineers?.length > 0) {
      const total = engItems?.reduce((s, e) => s + e?.sellTotal, 0);
      summaryRows?.push(['Professional Engineers', `${selectedEngineers?.length} resource(s)`, formatCurrency(total)]);
    }
    if (selectedMaterials?.length > 0) {
      const total = matItems?.reduce((s, m) => s + m?.sellTotal, 0);
      summaryRows?.push(['Materials & Equipment', `${selectedMaterials?.length} item(s)`, formatCurrency(total)]);
    }
    if (selectedLicenses?.length > 0) {
      const total = licItems?.reduce((s, l) => s + l?.sellTotal, 0);
      summaryRows?.push(['Software Licenses', `${selectedLicenses?.length} license(s)`, formatCurrency(total)]);
    }
    if (selectedOutsource?.length > 0) {
      const total = outItems?.reduce((s, o) => s + o?.sellTotal, 0);
      summaryRows?.push(['Outsource Resources', `${selectedOutsource?.length} resource(s)`, formatCurrency(total)]);
    }

    const grandTotal = computeSummaryTotals(engItems, matItems, licItems, outItems, calculations)?.sellPrice;
    summaryRows?.push([
      { content: 'GRAND TOTAL', styles: { fontStyle: 'bold', fillColor: NAVY, textColor: WHITE, fontSize: 10 } },
      { content: '', styles: { fillColor: NAVY } },
      { content: formatCurrency(grandTotal), styles: { fontStyle: 'bold', halign: 'right', fillColor: NAVY, textColor: [201, 168, 76], fontSize: 10 } },
    ]);

    doc?.autoTable({
      startY: yPos,
      head: [['Service Category', 'Scope', 'Amount (OMR)']],
      body: summaryRows,
      theme: 'grid',
      headStyles: { fillColor: [26, 47, 94], fontSize: 9, fontStyle: 'bold', textColor: WHITE },
      styles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 90 },
        1: { cellWidth: 50, halign: 'center' },
        2: { cellWidth: 40, halign: 'right', fontStyle: 'bold' },
      },
      alternateRowStyles: { fillColor: LIGHT_GREY },
    });

    yPos = doc?.lastAutoTable?.finalY + 14;

    // Grand total highlight box
    doc?.setFillColor(...NAVY);
    doc?.roundedRect(14, yPos, pageWidth - 28, 20, 3, 3, 'F');
    doc?.setTextColor(...GOLD_LIGHT);
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.text('TOTAL QUOTED VALUE (OMR)', 22, yPos + 13);
    doc?.setFontSize(13);
    doc?.setTextColor(...GOLD);
    doc?.text(formatCurrency(grandTotal), pageWidth - 22, yPos + 13, { align: 'right' });
    yPos += 30;

    // Terms & Conditions
    doc?.setFillColor(...LIGHT_GREY);
    doc?.roundedRect(14, yPos, pageWidth - 28, 44, 3, 3, 'F');
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...NAVY);
    doc?.text('Terms & Conditions', 20, yPos + 9);
    doc?.setFont('helvetica', 'normal');
    doc?.setFontSize(8);
    doc?.setTextColor(60, 60, 60);
    const terms = [
      '1. All prices are in Omani Rial (OMR) and are exclusive of applicable taxes unless stated otherwise.',
      '2. This quotation is valid for 30 days from the date of issue.',
      '3. Payment terms: 50% advance upon order confirmation, 50% upon project completion.',
      '4. Any scope changes will be subject to a revised quotation.',
    ];
    terms?.forEach((t, i) => {
      doc?.text(t, 20, yPos + 18 + i * 7);
    });
    yPos += 54;

    // Signature block
    doc?.setDrawColor(200, 200, 200);
    doc?.line(14, yPos, 80, yPos);
    doc?.line(pageWidth - 80, yPos, pageWidth - 14, yPos);
    doc?.setFontSize(8);
    doc?.setTextColor(120, 120, 120);
    doc?.text('Authorized Signature', 14, yPos + 6);
    doc?.text('Client Acceptance', pageWidth - 14, yPos + 6, { align: 'right' });

    // Add footers to all pages
    const totalPages = doc?.internal?.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc?.setPage(i);
      addFooter(i, totalPages);
    }

    doc?.save(`Quotation_${(estimateTitle || 'Quote')?.replace(/\s+/g, '_')}_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`);
    setIsExporting(false);
  };

  // ── Customer Excel (2 sheets: Quotation Summary, Itemized Scope) ─────────────
  const handleExportCustomerExcel = () => {
    const NAVY        = '0D1B3E';
    const NAVY_MID    = '1A2F5E';
    const NAVY_LIGHT  = '243B6E';
    const GOLD        = 'C9A84C';
    const GOLD_LIGHT  = 'F5E6B8';
    const WHITE       = 'FFFFFF';
    const LIGHT_GREY  = 'F4F6FB';
    const TEXT_DARK   = '0D1B3E';

    const ENG_BANNER  = '0D1B3E';
    const ENG_HEADER  = '1A2F5E';
    const ENG_ALT     = 'EBF0FA';
    const MAT_BANNER  = 'B45309';
    const MAT_HEADER  = 'D97706';
    const MAT_ALT     = 'FEF3C7';
    const LIC_BANNER  = '0F766E';
    const LIC_HEADER  = '0D9488';
    const LIC_ALT     = 'CCFBF1';
    const OUT_BANNER  = '6D28D9';
    const OUT_HEADER  = '7C3AED';
    const OUT_ALT     = 'EDE9FE';

    const border = {
      top:    { style: 'thin',   color: { rgb: 'C5CDE8' } },
      bottom: { style: 'thin',   color: { rgb: 'C5CDE8' } },
      left:   { style: 'thin',   color: { rgb: 'C5CDE8' } },
      right:  { style: 'thin',   color: { rgb: 'C5CDE8' } },
    };
    const thickBorder = {
      top:    { style: 'medium', color: { rgb: NAVY } },
      bottom: { style: 'medium', color: { rgb: NAVY } },
      left:   { style: 'medium', color: { rgb: NAVY } },
      right:  { style: 'medium', color: { rgb: NAVY } },
    };
    const goldBorder = {
      top:    { style: 'medium', color: { rgb: GOLD } },
      bottom: { style: 'medium', color: { rgb: GOLD } },
      left:   { style: 'medium', color: { rgb: GOLD } },
      right:  { style: 'medium', color: { rgb: GOLD } },
    };
    const makeBorder = (rgb) => ({
      top:    { style: 'medium', color: { rgb } },
      bottom: { style: 'medium', color: { rgb } },
      left:   { style: 'medium', color: { rgb } },
      right:  { style: 'medium', color: { rgb } },
    });

    const makeStyle = (fillRgb, fontObj, align = 'left', borders = border) => ({
      fill:      fillRgb ? { fgColor: { rgb: fillRgb } } : undefined,
      font:      fontObj,
      alignment: { horizontal: align, vertical: 'center', wrapText: true },
      border:    borders,
    });

    const titleFont        = { bold: true, color: { rgb: WHITE },      sz: 15 };
    const subTitleFont     = { bold: true, color: { rgb: GOLD },       sz: 12 };
    const metaFont         = { color: { rgb: GOLD_LIGHT },             sz: 9  };
    const bodyFont         = { color: { rgb: TEXT_DARK },              sz: 10 };
    const boldBodyFont     = { bold: true, color: { rgb: TEXT_DARK },  sz: 10 };
    const navyHeaderFont   = { bold: true, color: { rgb: WHITE },      sz: 10 };
    const totalFont        = { bold: true, color: { rgb: WHITE },      sz: 10 };
    const goldValueFont    = { bold: true, color: { rgb: GOLD },       sz: 11 };
    const sectionBannerFont = { bold: true, color: { rgb: WHITE },     sz: 11 };

    const COLS = ['A','B','C','D','E','F','G','H','I','J','K'];

    const { engItems: _cEng, matItems: _cMat, licItems: _cLic, outItems: _cOut } = getComputedItems();
    const _cst = computeSummaryTotals(_cEng, _cMat, _cLic, _cOut, calculations);
    const sellPrice = _cst?.sellPrice;
    const metaDate  = estimateDate
      ? new Date(estimateDate)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : '—';

    const writeBanner = (ws, merges, r, text, fillRgb, fontObj, numCols, brd) => {
      for (let c = 0; c < numCols; c++) {
        ws[`${COLS[c]}${r}`] = {
          v: c === 0 ? text : '', t: 's',
          s: makeStyle(fillRgb, c === 0 ? fontObj : {}, 'left', brd || thickBorder),
        };
      }
      merges?.push({ s: { r: r-1, c: 0 }, e: { r: r-1, c: numCols-1 } });
    };

    const writeHeaderRow = (ws, r, headers, fillRgb, fontObj, rightAlignFrom = 99, brd) => {
      headers?.forEach((h, i) => {
        ws[`${COLS[i]}${r}`] = {
          v: h, t: 's',
          s: makeStyle(fillRgb, fontObj, i >= rightAlignFrom ? 'right' : 'left', brd || thickBorder),
        };
      });
    };

    const { engItems, matItems, licItems, outItems } = getComputedItems();

    // ════════════════════════════════════════════════════════════════════════
    // SHEET 1 — QUOTATION SUMMARY
    // ════════════════════════════════════════════════════════════════════════
    const ws1 = {};
    const merges1 = [];
    let r1 = 1;

    const setCell1 = (col, row, value, style) => {
      ws1[`${col}${row}`] = { v: value, t: typeof value === 'number' ? 'n' : 's', s: style };
    };

    // Cover banner
    writeBanner(ws1, merges1, r1, COMPANY_NAME, NAVY, titleFont, 7, thickBorder);
    r1++;
    writeBanner(ws1, merges1, r1, 'SERVICE QUOTATION', NAVY, subTitleFont, 7, thickBorder);
    r1++;

    // Meta row
    setCell1('A', r1, `Quote Ref: ${estimateNo || '—'}`, makeStyle(NAVY_MID, metaFont, 'left', thickBorder));
    setCell1('B', r1, '', makeStyle(NAVY_MID, {}, 'left', thickBorder));
    setCell1('C', r1, `PTC: ${ptcNumber || '—'}`, makeStyle(NAVY_MID, metaFont, 'center', thickBorder));
    setCell1('D', r1, '', makeStyle(NAVY_MID, {}, 'center', thickBorder));
    setCell1('E', r1, `Date: ${metaDate}`, makeStyle(NAVY_MID, metaFont, 'right', thickBorder));
    setCell1('F', r1, '', makeStyle(NAVY_MID, {}, 'right', thickBorder));
    setCell1('G', r1, `Validity: 30 Days`, makeStyle(NAVY_MID, metaFont, 'right', thickBorder));
    merges1?.push({ s: { r: r1-1, c: 0 }, e: { r: r1-1, c: 1 } });
    merges1?.push({ s: { r: r1-1, c: 2 }, e: { r: r1-1, c: 3 } });
    merges1?.push({ s: { r: r1-1, c: 4 }, e: { r: r1-1, c: 5 } });
    r1++;

    // Project row
    writeBanner(ws1, merges1, r1, `Project: ${estimateTitle || '—'}   ·   Delivery Mode: ${(deliveryMode || 'Standard')?.charAt(0)?.toUpperCase() + (deliveryMode || 'Standard')?.slice(1)}`, NAVY_LIGHT, { color: { rgb: GOLD_LIGHT }, sz: 9 }, 7, thickBorder);
    r1++;

    // Spacer
    for (let c = 0; c < 7; c++) setCell1(COLS?.[c], r1, '', {});
    r1++;

    // Grand total KPI card
    writeBanner(ws1, merges1, r1, '  TOTAL QUOTED VALUE', GOLD, { bold: true, color: { rgb: NAVY }, sz: 11 }, 7, goldBorder);
    r1++;

    // Label row
    for (let c = 0; c < 7; c++) {
      setCell1(COLS?.[c], r1, c === 0 ? 'TOTAL QUOTED VALUE (OMR)' : '', makeStyle(NAVY, c === 0 ? { bold: true, color: { rgb: GOLD_LIGHT }, sz: 10 } : {}, 'left', thickBorder));
    }
    merges1?.push({ s: { r: r1-1, c: 0 }, e: { r: r1-1, c: 6 } });
    r1++;

    // Value row
    for (let c = 0; c < 7; c++) {
      setCell1(COLS?.[c], r1, c === 0 ? formatCurrency(sellPrice) : '', makeStyle(NAVY, c === 0 ? { bold: true, color: { rgb: GOLD }, sz: 16 } : {}, 'left', thickBorder));
    }
    merges1?.push({ s: { r: r1-1, c: 0 }, e: { r: r1-1, c: 6 } });
    r1 += 2;

    // Section breakdown banner
    writeBanner(ws1, merges1, r1, '  QUOTATION BREAKDOWN BY SERVICE CATEGORY', NAVY, sectionBannerFont, 7, thickBorder);
    r1++;

    // Table header
    writeHeaderRow(ws1, r1, ['Service Category', 'Description', 'Scope', 'Amount (OMR)', '', '', ''], NAVY_MID, navyHeaderFont, 3, thickBorder);
    merges1?.push({ s: { r: r1-1, c: 3 }, e: { r: r1-1, c: 6 } });
    r1++;

    let altRow = false;
    const summaryItems = [];
    if (selectedEngineers?.length > 0) {
      const total = engItems?.reduce((s, e) => s + e?.sellTotal, 0);
      summaryItems?.push({ cat: 'Professional Engineers', desc: 'Engineering & Technical Resources', scope: `${selectedEngineers?.length} resource(s)`, total });
    }
    if (selectedMaterials?.length > 0) {
      const total = matItems?.reduce((s, m) => s + m?.sellTotal, 0);
      summaryItems?.push({ cat: 'Materials & Equipment', desc: 'Hardware, Equipment & Consumables', scope: `${selectedMaterials?.length} item(s)`, total });
    }
    if (selectedLicenses?.length > 0) {
      const total = licItems?.reduce((s, l) => s + l?.sellTotal, 0);
      summaryItems?.push({ cat: 'Software Licenses', desc: 'Software & Subscription Licenses', scope: `${selectedLicenses?.length} license(s)`, total });
    }
    if (selectedOutsource?.length > 0) {
      const total = outItems?.reduce((s, o) => s + o?.sellTotal, 0);
      summaryItems?.push({ cat: 'Outsource Resources', desc: 'Third-Party & Contracted Resources', scope: `${selectedOutsource?.length} resource(s)`, total });
    }

    summaryItems?.forEach(item => {
      const bg = altRow ? LIGHT_GREY : WHITE;
      setCell1('A', r1, item?.cat,   makeStyle(bg, boldBodyFont, 'left',  border));
      setCell1('B', r1, item?.desc,  makeStyle(bg, bodyFont,     'left',  border));
      setCell1('C', r1, item?.scope, makeStyle(bg, bodyFont,     'center',border));
      setCell1('D', r1, formatCurrency(item?.total), makeStyle(bg, { bold: true, color: { rgb: NAVY }, sz: 10 }, 'right', border));
      setCell1('E', r1, '', makeStyle(bg, {}, 'left', border));
      setCell1('F', r1, '', makeStyle(bg, {}, 'left', border));
      setCell1('G', r1, '', makeStyle(bg, {}, 'left', border));
      merges1?.push({ s: { r: r1-1, c: 3 }, e: { r: r1-1, c: 6 } });
      altRow = !altRow;
      r1++;
    });

    // Grand total row
    for (let c = 0; c < 3; c++) setCell1(COLS?.[c], r1, c === 0 ? 'GRAND TOTAL' : '', makeStyle(NAVY, c === 0 ? totalFont : {}, 'left', thickBorder));
    setCell1('D', r1, formatCurrency(sellPrice), makeStyle(NAVY, goldValueFont, 'right', thickBorder));
    setCell1('E', r1, '', makeStyle(NAVY, {}, 'left', thickBorder));
    setCell1('F', r1, '', makeStyle(NAVY, {}, 'left', thickBorder));
    setCell1('G', r1, '', makeStyle(NAVY, {}, 'left', thickBorder));
    merges1?.push({ s: { r: r1-1, c: 0 }, e: { r: r1-1, c: 2 } });
    merges1?.push({ s: { r: r1-1, c: 3 }, e: { r: r1-1, c: 6 } });
    r1 += 2;

    // Terms
    writeBanner(ws1, merges1, r1, '  TERMS & CONDITIONS', GOLD, { bold: true, color: { rgb: NAVY }, sz: 10 }, 7, goldBorder);
    r1++;
    const termsRows = [
      'All prices are in Omani Rial (OMR) and are exclusive of applicable taxes unless stated otherwise.',
      'This quotation is valid for 30 days from the date of issue.',
      'Payment terms: 50% advance upon order confirmation, 50% upon project completion.',
      'Any scope changes will be subject to a revised quotation.',
    ];
    termsRows?.forEach((t, i) => {
      setCell1('A', r1, `${i+1}. ${t}`, makeStyle(i % 2 === 0 ? LIGHT_GREY : WHITE, { color: { rgb: TEXT_DARK }, sz: 9 }, 'left', border));
      for (let c = 1; c < 7; c++) setCell1(COLS?.[c], r1, '', makeStyle(i % 2 === 0 ? LIGHT_GREY : WHITE, {}, 'left', border));
      merges1?.push({ s: { r: r1-1, c: 0 }, e: { r: r1-1, c: 6 } });
      r1++;
    });
    r1++;

    // Footer
    writeBanner(ws1, merges1, r1, COMPANY_NAME, NAVY, { bold: true, color: { rgb: GOLD }, sz: 9 }, 6, thickBorder);
    setCell1('G', r1, `Generated: ${new Date()?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, makeStyle(NAVY, { color: { rgb: GOLD_LIGHT }, sz: 9 }, 'right', thickBorder));

    ws1['!ref']    = XLSX?.utils?.encode_range({ s: { r: 0, c: 0 }, e: { r: r1, c: 6 } });
    ws1['!merges'] = merges1;
    ws1['!cols']   = [{ wch: 30 }, { wch: 32 }, { wch: 16 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 18 }];
    ws1['!rows']   = Array.from({ length: r1 + 1 }, (_, i) => ({ hpt: i < 2 ? 28 : i < 5 ? 20 : 18 }));

    // ════════════════════════════════════════════════════════════════════════
    // SHEET 2 — ITEMIZED SCOPE
    // ════════════════════════════════════════════════════════════════════════
    const ws2 = {};
    const merges2 = [];
    let r2 = 1;

    const setCell2 = (col, row, value, style) => {
      ws2[`${col}${row}`] = { v: value, t: typeof value === 'number' ? 'n' : 's', s: style };
    };

    // Cover banner
    writeBanner(ws2, merges2, r2, COMPANY_NAME, NAVY, titleFont, 8, thickBorder);
    r2++;
    writeBanner(ws2, merges2, r2, 'ITEMIZED SCOPE OF SERVICES', NAVY, subTitleFont, 8, thickBorder);
    r2++;
    setCell2('A', r2, `${estimateTitle || '—'}   ·   ${ptcNumber || '—'}   ·   ${metaDate}`, makeStyle(NAVY_MID, metaFont, 'left', thickBorder));
    for (let c = 1; c < 8; c++) setCell2(COLS?.[c], r2, '', makeStyle(NAVY_MID, {}, 'left', thickBorder));
    merges2?.push({ s: { r: r2-1, c: 0 }, e: { r: r2-1, c: 7 } });
    r2++;
    for (let c = 0; c < 8; c++) setCell2(COLS?.[c], r2, '', {});
    r2++;

    // ── ENGINEERS ─────────────────────────────────────────────────────────────
    if (selectedEngineers?.length > 0) {
      writeBanner(ws2, merges2, r2, '  ▶  PROFESSIONAL ENGINEERS', ENG_BANNER, sectionBannerFont, 9, makeBorder(ENG_BANNER));
      r2++;

      writeHeaderRow(ws2, r2,
        ['Engineer / Role', 'Specialization', 'Count', 'Duration (hrs)', 'Sell/Unit/Month (OMR)', 'Line Total (OMR)', ''],
        ENG_HEADER, navyHeaderFont, 2, makeBorder(ENG_HEADER)
      );
      r2++;

      let totalEngSell = 0;
      engItems?.forEach((e, idx) => {
        totalEngSell += e?.sellTotal;
        const bg = idx % 2 === 0 ? WHITE : ENG_ALT;
        setCell2('A', r2, e?.name,                                makeStyle(bg, bodyFont,     'left',  border));
        setCell2('B', r2, e?.specialization || '—',               makeStyle(bg, bodyFont,     'left',  border));
        setCell2('C', r2, e?.count,                               makeStyle(bg, bodyFont,     'center',border));
        setCell2('D', r2, e?.hours,                               makeStyle(bg, bodyFont,     'center',border));
        setCell2('E', r2, formatCurrency(e?.sellPricePerUnitMonth), makeStyle(bg, { bold: true, color: { rgb: GOLD }, sz: 10 }, 'right', border));
        setCell2('F', r2, formatCurrency(e?.sellTotal),           makeStyle(bg, boldBodyFont, 'right', border));
        setCell2('G', r2, '',                                     makeStyle(bg, {}, 'left', border));
        r2++;
      });

      for (let c = 0; c < 5; c++) setCell2(COLS?.[c], r2, c === 0 ? 'Subtotal — Engineers' : '', makeStyle(ENG_BANNER, c === 0 ? totalFont : {}, 'left', makeBorder(ENG_BANNER)));
      setCell2('F', r2, formatCurrency(totalEngSell), makeStyle(ENG_BANNER, goldValueFont, 'right', makeBorder(ENG_BANNER)));
      setCell2('G', r2, '', makeStyle(ENG_BANNER, {}, 'left', makeBorder(ENG_BANNER)));
      merges2?.push({ s: { r: r2-1, c: 0 }, e: { r: r2-1, c: 4 } });
      r2 += 2;
    }

    // ── MATERIALS ─────────────────────────────────────────────────────────────
    if (selectedMaterials?.length > 0) {
      writeBanner(ws2, merges2, r2, '  ▶  MATERIALS & EQUIPMENT', MAT_BANNER, sectionBannerFont, 5, makeBorder(MAT_BANNER));
      r2++;

      writeHeaderRow(ws2, r2,
        ['Material / Item', 'Quantity', 'Sell/Unit/Month (OMR)', 'Line Total (OMR)'],
        MAT_HEADER, navyHeaderFont, 1, makeBorder(MAT_HEADER)
      );
      r2++;

      let totalMatSell = 0;
      matItems?.forEach((m, idx) => {
        totalMatSell += m?.sellTotal;
        const bg = idx % 2 === 0 ? WHITE : MAT_ALT;
        setCell2('A', r2, m?.name,                              makeStyle(bg, bodyFont,     'left',  border));
        setCell2('B', r2, m?.qty,                               makeStyle(bg, bodyFont,     'center',border));
        setCell2('C', r2, formatCurrency(m?.sellPricePerUnitMonth), makeStyle(bg, { bold: true, color: { rgb: GOLD }, sz: 10 }, 'right', border));
        setCell2('D', r2, formatCurrency(m?.sellTotal),         makeStyle(bg, boldBodyFont, 'right', border));
        r2++;
      });

      for (let c = 0; c < 3; c++) setCell2(COLS?.[c], r2, c === 0 ? 'Subtotal — Materials' : '', makeStyle(MAT_BANNER, c === 0 ? totalFont : {}, 'left', makeBorder(MAT_BANNER)));
      setCell2('D', r2, formatCurrency(totalMatSell), makeStyle(MAT_BANNER, goldValueFont, 'right', makeBorder(MAT_BANNER)));
      merges2?.push({ s: { r: r2-1, c: 0 }, e: { r: r2-1, c: 2 } });
      r2 += 2;
    }

    // ── LICENSES ──────────────────────────────────────────────────────────────
    if (selectedLicenses?.length > 0) {
      writeBanner(ws2, merges2, r2, '  ▶  SOFTWARE LICENSES', LIC_BANNER, sectionBannerFont, 7, makeBorder(LIC_BANNER));
      r2++;

      writeHeaderRow(ws2, r2,
        ['License / Software', 'Vendor', 'Type', 'Qty / Duration', 'Sell/Unit/Month (OMR)', 'Line Total (OMR)'],
        LIC_HEADER, navyHeaderFont, 3, makeBorder(LIC_HEADER)
      );
      r2++;

      let totalLicSell = 0;
      licItems?.forEach((l, idx) => {
        totalLicSell += l?.sellTotal;
        const bg = idx % 2 === 0 ? WHITE : LIC_ALT;
        const countDur = l?.type === 'Perpetual' ? `${l?.qty} unit(s)` : `${l?.qty} × ${l?.duration} mo`;
        setCell2('A', r2, l?.name,                              makeStyle(bg, bodyFont,     'left',  border));
        setCell2('B', r2, l?.vendor || '—',                     makeStyle(bg, bodyFont,     'left',  border));
        setCell2('C', r2, l?.type,                              makeStyle(bg, bodyFont,     'left',  border));
        setCell2('D', r2, countDur,                             makeStyle(bg, bodyFont,     'center',border));
        setCell2('E', r2, formatCurrency(l?.sellPricePerUnitMonth), makeStyle(bg, { bold: true, color: { rgb: GOLD }, sz: 10 }, 'right', border));
        setCell2('F', r2, formatCurrency(l?.sellTotal),         makeStyle(bg, boldBodyFont, 'right', border));
        r2++;
      });

      for (let c = 0; c < 5; c++) setCell2(COLS?.[c], r2, c === 0 ? 'Subtotal — Licenses' : '', makeStyle(LIC_BANNER, c === 0 ? totalFont : {}, 'left', makeBorder(LIC_BANNER)));
      setCell2('F', r2, formatCurrency(totalLicSell), makeStyle(LIC_BANNER, goldValueFont, 'right', makeBorder(LIC_BANNER)));
      merges2?.push({ s: { r: r2-1, c: 0 }, e: { r: r2-1, c: 4 } });
      r2 += 2;
    }

    // ── OUTSOURCE ─────────────────────────────────────────────────────────────
    if (selectedOutsource?.length > 0) {
      writeBanner(ws2, merges2, r2, '  ▶  OUTSOURCE RESOURCES', OUT_BANNER, sectionBannerFont, 9, makeBorder(OUT_BANNER));
      r2++;

      writeHeaderRow(ws2, r2,
        ['Resource', 'Role / Skill', 'Qty', 'Duration', 'Sell/Unit/Month (OMR)', 'Line Total (OMR)', ''],
        OUT_HEADER, navyHeaderFont, 2, makeBorder(OUT_HEADER)
      );
      r2++;

      let totalOutSell = 0;
      outItems?.forEach((o, idx) => {
        totalOutSell += o?.sellTotal;
        const bg = idx % 2 === 0 ? WHITE : OUT_ALT;
        setCell2('A', r2, o?.name,                                  makeStyle(bg, bodyFont,     'left',  border));
        setCell2('B', r2, o?.roleLabel || '—',                      makeStyle(bg, bodyFont,     'left',  border));
        setCell2('C', r2, o?.qty,                                   makeStyle(bg, bodyFont,     'center',border));
        setCell2('D', r2, `${o?.duration} ${o?.timeUnitLabel}`,     makeStyle(bg, bodyFont,     'center',border));
        setCell2('E', r2, formatCurrency(o?.sellPricePerUnitMonth), makeStyle(bg, { bold: true, color: { rgb: GOLD }, sz: 10 }, 'right', border));
        setCell2('F', r2, formatCurrency(o?.sellTotal),             makeStyle(bg, boldBodyFont, 'right', border));
        setCell2('G', r2, '',                                       makeStyle(bg, {}, 'left', border));
        r2++;
      });

      for (let c = 0; c < 5; c++) setCell2(COLS?.[c], r2, c === 0 ? 'Subtotal — Outsource' : '', makeStyle(OUT_BANNER, c === 0 ? totalFont : {}, 'left', makeBorder(OUT_BANNER)));
      setCell2('F', r2, formatCurrency(totalOutSell), makeStyle(OUT_BANNER, goldValueFont, 'right', makeBorder(OUT_BANNER)));
      setCell2('G', r2, '', makeStyle(OUT_BANNER, {}, 'left', makeBorder(OUT_BANNER)));
      merges2?.push({ s: { r: r2-1, c: 0 }, e: { r: r2-1, c: 4 } });
      r2 += 2;
    }

    // Grand total row
    writeBanner(ws2, merges2, r2, `GRAND TOTAL  ·  ${formatCurrency(sellPrice)}`, NAVY, { bold: true, color: { rgb: GOLD }, sz: 12 }, 7, thickBorder);
    r2 += 2;

    // Footer
    writeBanner(ws2, merges2, r2, COMPANY_NAME, NAVY, { bold: true, color: { rgb: GOLD }, sz: 9 }, 7, thickBorder);
    setCell2('G', r2, `Generated: ${new Date()?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, makeStyle(NAVY, { color: { rgb: GOLD_LIGHT }, sz: 9 }, 'right', thickBorder));

    ws2['!ref']    = XLSX?.utils?.encode_range({ s: { r: 0, c: 0 }, e: { r: r2, c: 6 } });
    ws2['!merges'] = merges2;
    ws2['!cols']   = [{ wch: 30 }, { wch: 24 }, { wch: 10 }, { wch: 18 }, { wch: 22 }, { wch: 20 }, { wch: 18 }];
    ws2['!rows']   = Array.from({ length: r2 + 1 }, (_, i) => ({ hpt: i < 3 ? 26 : 20 }));

    // Assemble workbook
    const wb = XLSX?.utils?.book_new();
    XLSX?.utils?.book_append_sheet(wb, ws1, 'Quotation Summary');
    XLSX?.utils?.book_append_sheet(wb, ws2, 'Itemized Scope');
    XLSX?.writeFile(wb, `Quotation_${(estimateTitle || 'Quote')?.replace(/\s+/g, '_')}_${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);
  };

  const handleExportWord = async () => {
    try {
      const { Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun, HeadingLevel, AlignmentType, WidthType, BorderStyle } = await import('docx');

      const borderStyle = {
        top: { style: BorderStyle?.SINGLE, size: 1, color: '2980B9' },
        bottom: { style: BorderStyle?.SINGLE, size: 1, color: '2980B9' },
        left: { style: BorderStyle?.SINGLE, size: 1, color: '2980B9' },
        right: { style: BorderStyle?.SINGLE, size: 1, color: '2980B9' },
      };
      const totalBorderStyle = {
        top: { style: BorderStyle?.SINGLE, size: 2, color: '1A5276' },
        bottom: { style: BorderStyle?.SINGLE, size: 2, color: '1A5276' },
        left: { style: BorderStyle?.SINGLE, size: 2, color: '1A5276' },
        right: { style: BorderStyle?.SINGLE, size: 2, color: '1A5276' },
      };

      const makeHeaderRow = (cells) => new TableRow({
        children: cells?.map(text => new TableCell({
          borders: borderStyle,
          shading: { fill: '2980B9' },
          children: [new Paragraph({ children: [new TextRun({ text: String(text), bold: true, color: 'FFFFFF', size: 18 })] })],
        })),
      });

      const makeDataRow = (cells, highlight = false) => new TableRow({
        children: cells?.map(text => new TableCell({
          borders: borderStyle,
          shading: highlight ? { fill: 'E8F4FD' } : undefined,
          children: [new Paragraph({ children: [new TextRun({ text: String(text ?? ''), size: 18, bold: highlight })] })],
        })),
      });

      const makeTotalRow = (cells) => new TableRow({
        children: cells?.map(text => new TableCell({
          borders: totalBorderStyle,
          shading: { fill: 'D6EAF8' },
          children: [new Paragraph({ children: [new TextRun({ text: String(text ?? ''), bold: true, color: '1A5276', size: 18 })] })],
        })),
      });

      const makeSectionRow = (label, colSpan = 2) => new TableRow({
        children: [new TableCell({
          columnSpan: colSpan,
          borders: borderStyle,
          shading: { fill: 'D6EAF8' },
          children: [new Paragraph({ children: [new TextRun({ text: label, bold: true, color: '1A5276', size: 20 })] })],
        })],
      });

      const { engItems, matItems, licItems, outItems } = getComputedItems();

      // ── Grand total summary table ──────────────────────────────────────────
      const _st = computeSummaryTotals(engItems, matItems, licItems, outItems, calculations);
      const directCosts = _st?.directCosts;
      const overheadCosts = _st?.overheadCosts;
      const contingencyCosts = _st?.contingencyCosts;
      const totalCost = _st?.totalCost;
      const sellPrice = _st?.sellPrice;
      const grossProfit = _st?.grossProfit;
      const profitMarginPct = _st?.profitMarginPercentage;

      const summaryTableRows = [
        makeHeaderRow(['Description', 'Cost (OMR)', 'Sell (OMR)']),
      ];
      if (engItems?.length > 0) {
        const ec = engItems?.reduce((s, e) => s + e?.costTotal, 0);
        const es = engItems?.reduce((s, e) => s + e?.sellTotal, 0);
        summaryTableRows?.push(makeDataRow(['Engineers', formatCurrency(ec), formatCurrency(es)]));
      }
      if (matItems?.length > 0) {
        const mc = matItems?.reduce((s, m) => s + m?.costTotal, 0);
        const ms = matItems?.reduce((s, m) => s + m?.sellTotal, 0);
        summaryTableRows?.push(makeDataRow(['Materials', formatCurrency(mc), formatCurrency(ms)]));
      }
      if (licItems?.length > 0) {
        const lc = licItems?.reduce((s, l) => s + l?.costTotal, 0);
        const ls = licItems?.reduce((s, l) => s + l?.sellTotal, 0);
        summaryTableRows?.push(makeDataRow(['Licenses', formatCurrency(lc), formatCurrency(ls)]));
      }
      if (outItems?.length > 0) {
        const oc = outItems?.reduce((s, o) => s + o?.costTotal, 0);
        const os = outItems?.reduce((s, o) => s + o?.sellTotal, 0);
        summaryTableRows?.push(makeDataRow(['Outsource Resources', formatCurrency(oc), formatCurrency(os)]));
      }
      summaryTableRows?.push(makeTotalRow(['Direct Costs', formatCurrency(directCosts), '—']));
      summaryTableRows?.push(makeDataRow([`Overhead (${calculations?.overheadPercentage || 0}%)`, formatCurrency(overheadCosts), '—']));
      summaryTableRows?.push(makeDataRow([`Contingency (${calculations?.contingencyPercentage || 0}%)`, formatCurrency(contingencyCosts), '—']));
      summaryTableRows?.push(makeTotalRow(['Total Project Cost', formatCurrency(totalCost), '—']));
      summaryTableRows?.push(makeTotalRow([`Sell Price (${calculations?.marginPercentage || 0}% margin)`, '—', formatCurrency(sellPrice)]));
      summaryTableRows?.push(makeDataRow(['Gross Profit', '—', formatCurrency(grossProfit)]));
      summaryTableRows?.push(makeDataRow([`Profit Margin`, '—', `${profitMarginPct?.toFixed(2)}%`]));

      const children = [
        new Paragraph({ text: COMPANY_NAME, heading: HeadingLevel.HEADING_1, alignment: AlignmentType.LEFT }),
        new Paragraph({
          children: [
            new TextRun({ text: estimateTitle, bold: true, size: 28 }),
            new TextRun({ text: `  |  Delivery Mode: ${deliveryMode?.charAt(0)?.toUpperCase() + deliveryMode?.slice(1)}`, size: 22, color: '555555' }),
          ],
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `PTC Number: ${ptcNumber || '—'}  |  `, size: 18, color: '555555' }),
            new TextRun({ text: `Generated: ${new Date()?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, size: 18, color: '888888' }),
          ],
        }),
        new Paragraph({ text: '' }),
        new Paragraph({ text: 'Calculation Summary', heading: HeadingLevel.HEADING_2 }),
        new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: summaryTableRows }),
        new Paragraph({ text: '' }),
      ];

      if (selectedEngineers?.length > 0) {
        const engRows = getEngineerRows();
        const engCostTotal = engRows?.reduce((s, e) => s + e?._costTotal, 0);
        const engSellTotal = engRows?.reduce((s, e) => s + e?._sellTotal, 0);
        children?.push(new Paragraph({ text: 'Required Engineers', heading: HeadingLevel.HEADING_2 }));
        children?.push(new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            makeHeaderRow(['Engineer', 'Specialization', 'Nationality', 'Count', 'Hours', 'Cost/Hr', 'SPF', 'Health Ins.', 'Cost Total', 'Sell Total']),
            ...engRows?.map(e => makeDataRow([e?.name, e?.specialization, e?.nationality, e?.count, e?.hours, e?.costPerHour, e?.spf, e?.healthIns, e?.total, formatCurrency(e?._sellTotal)])),
            makeTotalRow(['TOTAL', '', '', '', '', '', '', '', formatCurrency(engCostTotal), formatCurrency(engSellTotal)]),
          ],
        }));
        children?.push(new Paragraph({ text: '' }));
      }

      if (selectedMaterials?.length > 0) {
        const matRows = getMaterialRows();
        const matCostTotal = matRows?.reduce((s, m) => s + m?._costTotal, 0);
        const matSellTotal = matRows?.reduce((s, m) => s + m?._sellTotal, 0);
        children?.push(new Paragraph({ text: 'Required Materials', heading: HeadingLevel.HEADING_2 }));
        children?.push(new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            makeHeaderRow(['Material', 'Quantity', 'Unit Cost (DB)', 'Waste %', 'Cost Total', 'Sell Total']),
            ...matRows?.map(m => makeDataRow([m?.name, m?.quantity, m?.unitCost, m?.waste, m?.total, formatCurrency(m?._sellTotal)])),
            makeTotalRow(['TOTAL', '', '', '', formatCurrency(matCostTotal), formatCurrency(matSellTotal)]),
          ],
        }));
        children?.push(new Paragraph({ text: '' }));
      }

      if (selectedLicenses?.length > 0) {
        const licRows = getLicenseRows();
        const licCostTotal = licRows?.reduce((s, l) => s + l?.totalRaw, 0);
        const licSellTotal = licRows?.reduce((s, l) => s + l?._sellTotal, 0);
        children?.push(new Paragraph({ text: 'Required Licenses', heading: HeadingLevel.HEADING_2 }));
        children?.push(new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            makeHeaderRow(['License', 'Type', 'Quantity', 'Duration (mo)', 'Cost', 'Cost Total', 'Sell Total']),
            ...licRows?.map(l => makeDataRow([l?.name, l?.type, l?.quantity, l?.duration, l?.unitCost, l?.total, formatCurrency(l?._sellTotal)])),
            makeTotalRow(['TOTAL', '', '', '', '', formatCurrency(licCostTotal), formatCurrency(licSellTotal)]),
          ],
        }));
        children?.push(new Paragraph({ text: '' }));
      }

      if (selectedOutsource?.length > 0) {
        const outRows = getOutsourceRows();
        const outCostTotal = outRows?.reduce((s, o) => s + o?._costTotal, 0);
        const outSellTotal = outRows?.reduce((s, o) => s + o?._sellTotal, 0);
        children?.push(new Paragraph({ text: 'Outsource Resources', heading: HeadingLevel.HEADING_2 }));
        children?.push(new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            makeHeaderRow(['Resource', 'Role/Skill', 'Nationality', 'Monthly Salary', 'Duration', 'Qty', 'Cost/Hr', 'SPF', 'Health Ins.', 'Cost Total', 'Sell Total']),
            ...outRows?.map(o => makeDataRow([o?.name, o?.roleSkill, o?.nationality, o?.monthlySalary, o?.duration, o?.quantity, o?.costPerHour, o?.spfCost, o?.healthInsurance, o?.total, formatCurrency(o?._sellTotal)])),
            makeTotalRow(['TOTAL', '', '', '', '', '', '', '', '', formatCurrency(outCostTotal), formatCurrency(outSellTotal)]),
          ],
        }));
        children?.push(new Paragraph({ text: '' }));
      }

      const doc = new Document({ sections: [{ children }] });
      const blob = await Packer?.toBlob(doc);
      const url = URL?.createObjectURL(blob);
      const a = document?.createElement('a');
      a.href = url;
      a.download = `${estimateTitle?.replace(/\s+/g, '_')}_${new Date()?.toISOString()?.split('T')?.[0]}.docx`;
      a?.click();
      URL?.revokeObjectURL(url);
    } catch (err) {
      console.error('Word export failed:', err);
      alert('Word export failed. Please try PDF or Excel instead.');
    }
  };

  const handleExportExcel = () => {
    const NAVY        = '0D1B3E';
    const NAVY_MID    = '1A2F5E';
    const NAVY_LIGHT  = '243B6E';
    const GOLD        = 'C9A84C';
    const GOLD_LIGHT  = 'F5E6B8';
    const WHITE       = 'FFFFFF';
    const LIGHT_GREY  = 'F4F6FB';
    const TEXT_DARK   = '0D1B3E';
    const GREEN       = '16A34A';
    const GREEN_LIGHT = 'DCFCE7';
    const PURPLE      = '7C3AED';
    const PURPLE_LIGHT = 'EDE9FE';
    const ORANGE      = 'D97706';
    const ORANGE_LIGHT = 'FEF3C7';
    const TEAL        = '0D9488';
    const TEAL_LIGHT  = 'CCFBF1';

    const border = {
      top:    { style: 'thin',   color: { rgb: 'C5CDE8' } },
      bottom: { style: 'thin',   color: { rgb: 'C5CDE8' } },
      left:   { style: 'thin',   color: { rgb: 'C5CDE8' } },
      right:  { style: 'thin',   color: { rgb: 'C5CDE8' } },
    };
    const thickBorder = {
      top:    { style: 'medium', color: { rgb: NAVY } },
      bottom: { style: 'medium', color: { rgb: NAVY } },
      left:   { style: 'medium', color: { rgb: NAVY } },
      right:  { style: 'medium', color: { rgb: NAVY } },
    };
    const goldBorder = {
      top:    { style: 'medium', color: { rgb: GOLD } },
      bottom: { style: 'medium', color: { rgb: GOLD } },
      left:   { style: 'medium', color: { rgb: GOLD } },
      right:  { style: 'medium', color: { rgb: GOLD } },
    };
    const makeBorder = (rgb) => ({
      top:    { style: 'medium', color: { rgb } },
      bottom: { style: 'medium', color: { rgb } },
      left:   { style: 'medium', color: { rgb } },
      right:  { style: 'medium', color: { rgb } },
    });

    const makeStyle = (fillRgb, fontObj, align = 'left', borders = border) => ({
      fill:      fillRgb ? { fgColor: { rgb: fillRgb } } : undefined,
      font:      fontObj,
      alignment: { horizontal: align, vertical: 'center', wrapText: true },
      border:    borders,
    });

    const titleFont        = { bold: true, color: { rgb: WHITE },      sz: 15 };
    const subTitleFont     = { bold: true, color: { rgb: GOLD },       sz: 12 };
    const metaFont         = { color: { rgb: GOLD_LIGHT },             sz: 9  };
    const bodyFont         = { color: { rgb: TEXT_DARK },              sz: 10 };
    const boldBodyFont     = { bold: true, color: { rgb: TEXT_DARK },  sz: 10 };
    const navyHeaderFont   = { bold: true, color: { rgb: WHITE },      sz: 10 };
    const totalFont        = { bold: true, color: { rgb: WHITE },      sz: 10 };
    const goldValueFont    = { bold: true, color: { rgb: GOLD },       sz: 11 };
    const sectionBannerFont = { bold: true, color: { rgb: WHITE },     sz: 11 };
    const costHeaderFont   = { bold: true, color: { rgb: WHITE },      sz: 9  };
    const sellHeaderFont   = { bold: true, color: { rgb: GOLD },       sz: 9  };
    const sellValueFont    = { bold: true, color: { rgb: GOLD },       sz: 10 };
    const greenValueFont   = { bold: true, color: { rgb: GREEN },      sz: 10 };

    const COLS = ['A','B','C','D','E','F','G','H','I','J','K','L','M','N'];

    const { engItems, matItems, licItems, outItems } = getComputedItems();
    const _st = computeSummaryTotals(engItems, matItems, licItems, outItems, calculations);
    const directCosts      = _st?.directCosts;
    const overheadCosts    = _st?.overheadCosts;
    const contingencyCosts = _st?.contingencyCosts;
    const totalCost        = _st?.totalCost;
    const sellPrice        = _st?.sellPrice;
    const grossProfit      = _st?.grossProfit;
    const profitMarginPct  = _st?.profitMarginPercentage;
    const engCostSum       = _st?.engCostSum;
    const matCostSum       = _st?.matCostSum;
    const licCostSum       = _st?.licCostSum;
    const outCostSum       = _st?.outCostSum;
    const engSellSum       = engItems?.reduce((s, e) => s + e?.sellTotal, 0) || 0;
    const matSellSum       = matItems?.reduce((s, m) => s + m?.sellTotal, 0) || 0;
    const licSellSum       = licItems?.reduce((s, l) => s + l?.sellTotal, 0) || 0;
    const outSellSum       = outItems?.reduce((s, o) => s + o?.sellTotal, 0) || 0;

    const metaDate = estimateDate
      ? new Date(estimateDate)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : '—';

    const writeBanner = (ws, merges, r, text, fillRgb, fontObj, numCols, brd) => {
      for (let c = 0; c < numCols; c++) {
        ws[`${COLS[c]}${r}`] = {
          v: c === 0 ? text : '', t: 's',
          s: makeStyle(fillRgb, c === 0 ? fontObj : {}, 'left', brd || thickBorder),
        };
      }
      merges?.push({ s: { r: r-1, c: 0 }, e: { r: r-1, c: numCols-1 } });
    };

    const wb = XLSX?.utils?.book_new();

    // ════════════════════════════════════════════════════════════════════════
    // SHEET 1 — EXECUTIVE SUMMARY
    // ════════════════════════════════════════════════════════════════════════
    const ws1 = {};
    const merges1 = [];
    let r1 = 1;

    const setCell1 = (col, row, value, style) => {
      ws1[`${col}${row}`] = { v: value, t: typeof value === 'number' ? 'n' : 's', s: style };
    };

    // Header banner
    writeBanner(ws1, merges1, r1, COMPANY_NAME, NAVY, titleFont, 8, thickBorder);
    r1++;
    writeBanner(ws1, merges1, r1, 'INTERNAL ESTIMATE — EXECUTIVE SUMMARY', NAVY, subTitleFont, 8, thickBorder);
    r1++;
    setCell1('A', r1, `PTC: ${estimateTitle || '—'}   ·   Ref: ${ptcNumber || '—'}   ·   ${metaDate}`, makeStyle(NAVY_MID, metaFont, 'left', thickBorder));
    for (let c = 1; c < 8; c++) setCell1(COLS?.[c], r1, '', makeStyle(NAVY_MID, {}, 'left', thickBorder));
    merges1?.push({ s: { r: r1-1, c: 0 }, e: { r: r1-1, c: 7 } });
    r1++;
    for (let c = 0; c < 8; c++) setCell1(COLS?.[c], r1, '', {});
    r1++;

    // KPI Cards row
    const kpis = [
      { label: 'Total Cost', value: formatCurrency(totalCost), fill: NAVY },
      { label: 'Sell Price', value: formatCurrency(sellPrice), fill: NAVY_MID },
      { label: 'Gross Profit', value: formatCurrency(grossProfit), fill: GREEN },
      { label: 'Margin %', value: `${profitMarginPct?.toFixed(2)}%`, fill: PURPLE },
      { label: `Overhead (${calculations?.overheadPercentage || 0}%)`, value: formatCurrency(overheadCosts), fill: ORANGE },
      { label: `Contingency (${calculations?.contingencyPercentage || 0}%)`, value: formatCurrency(contingencyCosts), fill: TEAL },
    ];
    // KPI label row
    kpis?.forEach((k, i) => {
      setCell1(COLS?.[i], r1, k?.label, makeStyle(k?.fill, { bold: true, color: { rgb: GOLD_LIGHT }, sz: 9 }, 'center', makeBorder(k?.fill)));
    });
    merges1?.push(); // no merge needed for individual KPI cells
    r1++;
    // KPI value row
    kpis?.forEach((k, i) => {
      setCell1(COLS?.[i], r1, k?.value, makeStyle(k?.fill, { bold: true, color: { rgb: GOLD }, sz: 12 }, 'center', makeBorder(k?.fill)));
    });
    r1 += 2;

    // Summary table header
    writeBanner(ws1, merges1, r1, '  COST & SELL SUMMARY BY SECTION', NAVY, sectionBannerFont, 8, thickBorder);
    r1++;

    // Column headers
    const summaryHeaders = ['Section', '', 'Cost (OMR)', '', 'Sell (OMR)', '', '', ''];
    summaryHeaders?.forEach((h, i) => {
      const isRight = i >= 2;
      setCell1(COLS?.[i], r1, h, makeStyle(NAVY_MID, navyHeaderFont, isRight ? 'right' : 'left', thickBorder));
    });
    merges1?.push({ s: { r: r1-1, c: 0 }, e: { r: r1-1, c: 1 } });
    merges1?.push({ s: { r: r1-1, c: 2 }, e: { r: r1-1, c: 3 } });
    merges1?.push({ s: { r: r1-1, c: 4 }, e: { r: r1-1, c: 7 } });
    r1++;

    const summaryRows = [];
    if (engItems?.length > 0)  summaryRows?.push({ label: 'Engineers',          cost: engCostSum,  sell: engSellSum  });
    if (matItems?.length > 0)  summaryRows?.push({ label: 'Materials',           cost: matCostSum,  sell: matSellSum  });
    if (licItems?.length > 0)  summaryRows?.push({ label: 'Licenses',            cost: licCostSum,  sell: licSellSum  });
    if (outItems?.length > 0)  summaryRows?.push({ label: 'Outsource Resources', cost: outCostSum,  sell: outSellSum  });

    summaryRows?.forEach((row, idx) => {
      const bg = idx % 2 === 0 ? WHITE : LIGHT_GREY;
      setCell1('A', r1, row?.label, makeStyle(bg, boldBodyFont, 'left', border));
      setCell1('B', r1, '', makeStyle(bg, {}, 'left', border));
      setCell1('C', r1, formatCurrency(row?.cost), makeStyle(bg, bodyFont, 'right', border));
      setCell1('D', r1, '', makeStyle(bg, {}, 'right', border));
      setCell1('E', r1, formatCurrency(row?.sell), makeStyle(bg, sellValueFont, 'right', border));
      setCell1('F', r1, '', makeStyle(bg, {}, 'right', border));
      setCell1('G', r1, '', makeStyle(bg, {}, 'right', border));
      setCell1('H', r1, '', makeStyle(bg, {}, 'right', border));
      merges1?.push({ s: { r: r1-1, c: 0 }, e: { r: r1-1, c: 1 } });
      merges1?.push({ s: { r: r1-1, c: 2 }, e: { r: r1-1, c: 3 } });
      merges1?.push({ s: { r: r1-1, c: 4 }, e: { r: r1-1, c: 7 } });
      r1++;
    });

    // Direct Costs row
    setCell1('A', r1, 'Direct Cost', makeStyle(NAVY_LIGHT, { bold: true, color: { rgb: WHITE }, sz: 10 }, 'left', thickBorder));
    setCell1('B', r1, '', makeStyle(NAVY_LIGHT, {}, 'left', thickBorder));
    setCell1('C', r1, formatCurrency(directCosts), makeStyle(NAVY_LIGHT, { bold: true, color: { rgb: WHITE }, sz: 10 }, 'right', thickBorder));
    setCell1('D', r1, '', makeStyle(NAVY_LIGHT, {}, 'right', thickBorder));
    setCell1('E', r1, '—', makeStyle(NAVY_LIGHT, { color: { rgb: GOLD_LIGHT }, sz: 10 }, 'right', thickBorder));
    setCell1('F', r1, '', makeStyle(NAVY_LIGHT, {}, 'right', thickBorder));
    setCell1('G', r1, '', makeStyle(NAVY_LIGHT, {}, 'right', thickBorder));
    setCell1('H', r1, '', makeStyle(NAVY_LIGHT, {}, 'right', thickBorder));
    merges1?.push({ s: { r: r1-1, c: 0 }, e: { r: r1-1, c: 1 } });
    merges1?.push({ s: { r: r1-1, c: 2 }, e: { r: r1-1, c: 3 } });
    merges1?.push({ s: { r: r1-1, c: 4 }, e: { r: r1-1, c: 7 } });
    r1++;

    // Overhead row
    setCell1('A', r1, `Overhead (${calculations?.overheadPercentage || 0}%)`, makeStyle(LIGHT_GREY, bodyFont, 'left', border));
    setCell1('B', r1, '', makeStyle(LIGHT_GREY, {}, 'left', border));
    setCell1('C', r1, formatCurrency(overheadCosts), makeStyle(LIGHT_GREY, bodyFont, 'right', border));
    setCell1('D', r1, '', makeStyle(LIGHT_GREY, {}, 'right', border));
    setCell1('E', r1, '—', makeStyle(LIGHT_GREY, bodyFont, 'right', border));
    setCell1('F', r1, '', makeStyle(LIGHT_GREY, {}, 'right', border));
    setCell1('G', r1, '', makeStyle(LIGHT_GREY, {}, 'right', border));
    setCell1('H', r1, '', makeStyle(LIGHT_GREY, {}, 'right', border));
    merges1?.push({ s: { r: r1-1, c: 0 }, e: { r: r1-1, c: 1 } });
    merges1?.push({ s: { r: r1-1, c: 2 }, e: { r: r1-1, c: 3 } });
    merges1?.push({ s: { r: r1-1, c: 4 }, e: { r: r1-1, c: 7 } });
    r1++;

    // Contingency row
    setCell1('A', r1, `Contingency (${calculations?.contingencyPercentage || 0}%)`, makeStyle(WHITE, bodyFont, 'left', border));
    setCell1('B', r1, '', makeStyle(WHITE, {}, 'left', border));
    setCell1('C', r1, formatCurrency(contingencyCosts), makeStyle(WHITE, bodyFont, 'right', border));
    setCell1('D', r1, '', makeStyle(WHITE, {}, 'right', border));
    setCell1('E', r1, '—', makeStyle(WHITE, bodyFont, 'right', border));
    setCell1('F', r1, '', makeStyle(WHITE, {}, 'right', border));
    setCell1('G', r1, '', makeStyle(WHITE, {}, 'right', border));
    setCell1('H', r1, '', makeStyle(WHITE, {}, 'right', border));
    merges1?.push({ s: { r: r1-1, c: 0 }, e: { r: r1-1, c: 1 } });
    merges1?.push({ s: { r: r1-1, c: 2 }, e: { r: r1-1, c: 3 } });
    merges1?.push({ s: { r: r1-1, c: 4 }, e: { r: r1-1, c: 7 } });
    r1++;

    // Total Cost row
    setCell1('A', r1, 'Total Cost', makeStyle(NAVY, { bold: true, color: { rgb: WHITE }, sz: 11 }, 'left', thickBorder));
    setCell1('B', r1, '', makeStyle(NAVY, {}, 'left', thickBorder));
    setCell1('C', r1, formatCurrency(totalCost), makeStyle(NAVY, { bold: true, color: { rgb: WHITE }, sz: 11 }, 'right', thickBorder));
    setCell1('D', r1, '', makeStyle(NAVY, {}, 'right', thickBorder));
    setCell1('E', r1, '—', makeStyle(NAVY, { color: { rgb: GOLD_LIGHT }, sz: 10 }, 'right', thickBorder));
    setCell1('F', r1, '', makeStyle(NAVY, {}, 'right', thickBorder));
    setCell1('G', r1, '', makeStyle(NAVY, {}, 'right', thickBorder));
    setCell1('H', r1, '', makeStyle(NAVY, {}, 'right', thickBorder));
    merges1?.push({ s: { r: r1-1, c: 0 }, e: { r: r1-1, c: 1 } });
    merges1?.push({ s: { r: r1-1, c: 2 }, e: { r: r1-1, c: 3 } });
    merges1?.push({ s: { r: r1-1, c: 4 }, e: { r: r1-1, c: 7 } });
    r1++;

    // Sell Price row
    setCell1('A', r1, `Sell Price (${calculations?.marginPercentage || 0}% margin)`, makeStyle(NAVY_MID, { bold: true, color: { rgb: GOLD_LIGHT }, sz: 10 }, 'left', thickBorder));
    setCell1('B', r1, '', makeStyle(NAVY_MID, {}, 'left', thickBorder));
    setCell1('C', r1, '—', makeStyle(NAVY_MID, { color: { rgb: GOLD_LIGHT }, sz: 10 }, 'right', thickBorder));
    setCell1('D', r1, '', makeStyle(NAVY_MID, {}, 'right', thickBorder));
    setCell1('E', r1, formatCurrency(sellPrice), makeStyle(NAVY_MID, { bold: true, color: { rgb: GOLD }, sz: 12 }, 'right', thickBorder));
    setCell1('F', r1, '', makeStyle(NAVY_MID, {}, 'right', thickBorder));
    setCell1('G', r1, '', makeStyle(NAVY_MID, {}, 'right', thickBorder));
    setCell1('H', r1, '', makeStyle(NAVY_MID, {}, 'right', thickBorder));
    merges1?.push({ s: { r: r1-1, c: 0 }, e: { r: r1-1, c: 1 } });
    merges1?.push({ s: { r: r1-1, c: 2 }, e: { r: r1-1, c: 3 } });
    merges1?.push({ s: { r: r1-1, c: 4 }, e: { r: r1-1, c: 7 } });
    r1++;

    // Gross Profit row
    setCell1('A', r1, 'Gross Profit', makeStyle(GREEN, { bold: true, color: { rgb: WHITE }, sz: 10 }, 'left', makeBorder(GREEN)));
    setCell1('B', r1, '', makeStyle(GREEN, {}, 'left', makeBorder(GREEN)));
    setCell1('C', r1, '—', makeStyle(GREEN, { color: { rgb: GREEN_LIGHT }, sz: 10 }, 'right', makeBorder(GREEN)));
    setCell1('D', r1, '', makeStyle(GREEN, {}, 'right', makeBorder(GREEN)));
    setCell1('E', r1, formatCurrency(grossProfit), makeStyle(GREEN, { bold: true, color: { rgb: WHITE }, sz: 11 }, 'right', makeBorder(GREEN)));
    setCell1('F', r1, '', makeStyle(GREEN, {}, 'right', makeBorder(GREEN)));
    setCell1('G', r1, '', makeStyle(GREEN, {}, 'right', makeBorder(GREEN)));
    setCell1('H', r1, '', makeStyle(GREEN, {}, 'right', makeBorder(GREEN)));
    merges1?.push({ s: { r: r1-1, c: 0 }, e: { r: r1-1, c: 1 } });
    merges1?.push({ s: { r: r1-1, c: 2 }, e: { r: r1-1, c: 3 } });
    merges1?.push({ s: { r: r1-1, c: 4 }, e: { r: r1-1, c: 7 } });
    r1++;

    // Margin % row
    setCell1('A', r1, 'Margin %', makeStyle(PURPLE, { bold: true, color: { rgb: WHITE }, sz: 10 }, 'left', makeBorder(PURPLE)));
    setCell1('B', r1, '', makeStyle(PURPLE, {}, 'left', makeBorder(PURPLE)));
    setCell1('C', r1, '—', makeStyle(PURPLE, { color: { rgb: PURPLE_LIGHT }, sz: 10 }, 'right', makeBorder(PURPLE)));
    setCell1('D', r1, '', makeStyle(PURPLE, {}, 'right', makeBorder(PURPLE)));
    setCell1('E', r1, `${profitMarginPct?.toFixed(2)}%`, makeStyle(PURPLE, { bold: true, color: { rgb: WHITE }, sz: 11 }, 'right', makeBorder(PURPLE)));
    setCell1('F', r1, '', makeStyle(PURPLE, {}, 'right', makeBorder(PURPLE)));
    setCell1('G', r1, '', makeStyle(PURPLE, {}, 'right', makeBorder(PURPLE)));
    setCell1('H', r1, '', makeStyle(PURPLE, {}, 'right', makeBorder(PURPLE)));
    merges1?.push({ s: { r: r1-1, c: 0 }, e: { r: r1-1, c: 1 } });
    merges1?.push({ s: { r: r1-1, c: 2 }, e: { r: r1-1, c: 3 } });
    merges1?.push({ s: { r: r1-1, c: 4 }, e: { r: r1-1, c: 7 } });
    r1 += 2;

    // Footer
    writeBanner(ws1, merges1, r1, `${COMPANY_NAME}   ·   CONFIDENTIAL — INTERNAL USE ONLY`, NAVY, { bold: true, color: { rgb: GOLD }, sz: 9 }, 8, thickBorder);

    ws1['!ref']    = XLSX?.utils?.encode_range({ s: { r: 0, c: 0 }, e: { r: r1, c: 7 } });
    ws1['!merges'] = merges1;
    ws1['!cols']   = [{ wch: 28 }, { wch: 4 }, { wch: 20 }, { wch: 4 }, { wch: 20 }, { wch: 4 }, { wch: 4 }, { wch: 4 }];
    ws1['!rows']   = Array.from({ length: r1 + 1 }, (_, i) => ({ hpt: i < 2 ? 28 : i < 5 ? 22 : 20 }));

    // ════════════════════════════════════════════════════════════════════════
    // SHEET 2 — ENGINEERS & OUTSOURCE
    // ════════════════════════════════════════════════════════════════════════
    const ws2 = {};
    const merges2 = [];
    let r2 = 1;

    const setCell2 = (col, row, value, style) => {
      ws2[`${col}${row}`] = { v: value, t: typeof value === 'number' ? 'n' : 's', s: style };
    };

    writeBanner(ws2, merges2, r2, COMPANY_NAME, NAVY, titleFont, 14, thickBorder);
    r2++;
    writeBanner(ws2, merges2, r2, 'ENGINEERS & OUTSOURCE RESOURCES — INTERNAL', NAVY, subTitleFont, 14, thickBorder);
    r2++;
    setCell2('A', r2, `${estimateTitle || '—'}   ·   ${ptcNumber || '—'}   ·   ${metaDate}`, makeStyle(NAVY_MID, metaFont, 'left', thickBorder));
    for (let c = 1; c < 14; c++) setCell2(COLS?.[c], r2, '', makeStyle(NAVY_MID, {}, 'left', thickBorder));
    merges2?.push({ s: { r: r2-1, c: 0 }, e: { r: r2-1, c: 13 } });
    r2++;
    for (let c = 0; c < 14; c++) setCell2(COLS?.[c], r2, '', {});
    r2++;

    // ── ENGINEERS ─────────────────────────────────────────────────────────────
    if (engItems?.length > 0) {
      writeBanner(ws2, merges2, r2, '  ▶  ENGINEERS', NAVY, sectionBannerFont, 14, makeBorder(NAVY));
      r2++;

      // ── GROUP HEADER ROW (COST / SELL) ──────────────────────────────────────
      // Cols 0-5 (A-F): identifier — navy, no label
      for (let c = 0; c < 6; c++) {
        setCell2(COLS?.[c], r2, '', makeStyle(NAVY, {}, 'center', thickBorder));
      }
      // Cols 6-9 (G-J): COST group — navy, white bold
      setCell2('G', r2, 'COST', makeStyle(NAVY, { bold: true, color: { rgb: WHITE }, sz: 11 }, 'center', thickBorder));
      for (let c = 7; c <= 9; c++) {
        setCell2(COLS?.[c], r2, '', makeStyle(NAVY, {}, 'center', thickBorder));
      }
      merges2?.push({ s: { r: r2-1, c: 6 }, e: { r: r2-1, c: 9 } });
      // Cols 10-12 (K-M): SELL group — gold, dark bold
      setCell2('K', r2, 'SELL', makeStyle(GOLD, { bold: true, color: { rgb: TEXT_DARK }, sz: 11 }, 'center', makeBorder(GOLD)));
      for (let c = 11; c <= 12; c++) {
        setCell2(COLS?.[c], r2, '', makeStyle(GOLD, {}, 'center', makeBorder(GOLD)));
      }
      merges2?.push({ s: { r: r2-1, c: 10 }, e: { r: r2-1, c: 12 } });
      // Col 13 (N): spacer
      setCell2('N', r2, '', makeStyle(NAVY, {}, 'left', thickBorder));
      r2++;

      // Column headers — Cost side + Sell side
      const engHeaders = [
        'Name', 'Role', 'Specialization', 'Nationality', 'Count', 'Hours',
        'Monthly Salary (OMR)', 'SPF (OMR)', 'Health Ins. (OMR)', 'Cost Total (OMR)',
        'Unit Rate (OMR/hr)', 'Sell/Unit/Month (OMR)', 'Line Sell Total (OMR)', '',
      ];
      engHeaders?.forEach((h, i) => {
        const isSell = i >= 10;
        setCell2(COLS?.[i], r2, h, makeStyle(isSell ? NAVY_MID : NAVY_LIGHT, isSell ? sellHeaderFont : costHeaderFont, i >= 6 ? 'right' : 'left', thickBorder));
      });
      r2++;

      let totalEngCost = 0;
      let totalEngSell = 0;
      engItems?.forEach((e, idx) => {
        totalEngCost += e?.costTotal;
        totalEngSell += e?.sellTotal;
        const bg = idx % 2 === 0 ? WHITE : LIGHT_GREY;
        setCell2('A', r2, e?.name,                                makeStyle(bg, bodyFont,     'left',  border));
        setCell2('B', r2, e?.name || '—',                         makeStyle(bg, bodyFont,     'left',  border));
        setCell2('C', r2, e?.specialization || '—',               makeStyle(bg, bodyFont,     'left',  border));
        setCell2('D', r2, e?.nationality,                         makeStyle(bg, bodyFont,     'left',  border));
        setCell2('E', r2, e?.count,                               makeStyle(bg, bodyFont,     'center',border));
        setCell2('F', r2, e?.hours,                               makeStyle(bg, bodyFont,     'center',border));
        setCell2('G', r2, formatCurrency(e?.monthlySalary),       makeStyle(bg, bodyFont,     'right', border));
        setCell2('H', r2, e?.nationality === 'Omani' ? formatCurrency(e?.spfValue) : '—', makeStyle(bg, bodyFont, 'right', border));
        setCell2('I', r2, e?.healthInsuranceEnabled ? formatCurrency(e?.healthInsurance) : '—', makeStyle(bg, bodyFont, 'right', border));
        setCell2('J', r2, formatCurrency(e?.costTotal),           makeStyle(bg, boldBodyFont, 'right', border));
        setCell2('K', r2, formatCurrency(e?.sellCostPerHour),     makeStyle(bg, sellValueFont,'right', border));
        setCell2('L', r2, formatCurrency(e?.sellPricePerUnitMonth), makeStyle(bg, sellValueFont, 'right', border));
        setCell2('M', r2, formatCurrency(e?.sellTotal),           makeStyle(bg, sellValueFont,'right', border));
        setCell2('N', r2, '',                                     makeStyle(bg, {}, 'left', border));
        r2++;
      });

      // Total row
      const engTotalCells = ['TOTAL', '', '', '', '', '', '', '', '', formatCurrency(totalEngCost), '', '', formatCurrency(totalEngSell), ''];
      engTotalCells?.forEach((v, i) => {
        const isSell = i >= 10;
        setCell2(COLS?.[i], r2, v, makeStyle(isSell ? NAVY_MID : NAVY, isSell ? goldValueFont : totalFont, i >= 6 ? 'right' : 'left', thickBorder));
      });
      merges2?.push({ s: { r: r2-1, c: 0 }, e: { r: r2-1, c: 8 } });
      r2 += 2;
    }

    // ── OUTSOURCE ─────────────────────────────────────────────────────────────
    if (outItems?.length > 0) {
      writeBanner(ws2, merges2, r2, '  ▶  OUTSOURCE RESOURCES', NAVY_LIGHT, sectionBannerFont, 14, makeBorder(NAVY_LIGHT));
      r2++;

      // ── GROUP HEADER ROW (COST / SELL) ──────────────────────────────────────
      for (let c = 0; c < 6; c++) {
        setCell2(COLS?.[c], r2, '', makeStyle(NAVY, {}, 'center', thickBorder));
      }
      setCell2('G', r2, 'COST', makeStyle(NAVY, { bold: true, color: { rgb: WHITE }, sz: 11 }, 'center', thickBorder));
      for (let c = 7; c <= 9; c++) {
        setCell2(COLS?.[c], r2, '', makeStyle(NAVY, {}, 'center', thickBorder));
      }
      merges2?.push({ s: { r: r2-1, c: 6 }, e: { r: r2-1, c: 9 } });
      setCell2('K', r2, 'SELL', makeStyle(GOLD, { bold: true, color: { rgb: TEXT_DARK }, sz: 11 }, 'center', makeBorder(GOLD)));
      for (let c = 11; c <= 12; c++) {
        setCell2(COLS?.[c], r2, '', makeStyle(GOLD, {}, 'center', makeBorder(GOLD)));
      }
      merges2?.push({ s: { r: r2-1, c: 10 }, e: { r: r2-1, c: 12 } });
      setCell2('N', r2, '', makeStyle(NAVY, {}, 'left', thickBorder));
      r2++;

      const outHeaders = [
        'Name', 'Role', 'Specialization', 'Nationality', 'Count', 'Hours',
        'Monthly Salary (OMR)', 'SPF (OMR)', 'Health Ins. (OMR)', 'Cost Total (OMR)',
        'Unit Rate (OMR/hr)', 'Sell/Unit/Month (OMR)', 'Line Sell Total (OMR)', '',
      ];
      outHeaders?.forEach((h, i) => {
        const isSell = i >= 10;
        setCell2(COLS?.[i], r2, h, makeStyle(isSell ? NAVY_MID : NAVY_LIGHT, isSell ? sellHeaderFont : costHeaderFont, i >= 6 ? 'right' : 'left', thickBorder));
      });
      r2++;

      let totalOutCost = 0;
      let totalOutSell = 0;
      outItems?.forEach((o, idx) => {
        totalOutCost += o?.costTotal;
        totalOutSell += o?.sellTotal;
        const bg = idx % 2 === 0 ? WHITE : LIGHT_GREY;
        setCell2('A', r2, o?.name,                                makeStyle(bg, bodyFont,     'left',  border));
        setCell2('B', r2, o?.roleLabel || '—',                    makeStyle(bg, bodyFont,     'left',  border));
        setCell2('C', r2, '',                                     makeStyle(bg, bodyFont,     'left',  border));
        setCell2('D', r2, o?.nationality,                         makeStyle(bg, bodyFont,     'left',  border));
        setCell2('E', r2, o?.qty,                                 makeStyle(bg, bodyFont,     'center',border));
        setCell2('F', r2, o?.hours,                               makeStyle(bg, bodyFont,     'center',border));
        setCell2('G', r2, formatCurrency(o?.monthlySalary),       makeStyle(bg, bodyFont,     'right', border));
        setCell2('H', r2, o?.isOmani ? formatCurrency(o?.spfCost) : '—', makeStyle(bg, bodyFont, 'right', border));
        setCell2('I', r2, o?.healthInsuranceEnabled ? formatCurrency(o?.healthInsurance) : '—', makeStyle(bg, bodyFont, 'right', border));
        setCell2('J', r2, formatCurrency(o?.costTotal),           makeStyle(bg, boldBodyFont, 'right', border));
        setCell2('K', r2, formatCurrency(o?.sellCostPerHour),     makeStyle(bg, sellValueFont,'right', border));
        setCell2('L', r2, formatCurrency(o?.sellPricePerUnitMonth), makeStyle(bg, sellValueFont, 'right', border));
        setCell2('M', r2, formatCurrency(o?.sellTotal),           makeStyle(bg, sellValueFont,'right', border));
        setCell2('N', r2, '',                                     makeStyle(bg, {}, 'left', border));
        r2++;
      });

      const outTotalCells = ['TOTAL', '', '', '', '', '', '', '', '', formatCurrency(totalOutCost), '', '', formatCurrency(totalOutSell), ''];
      outTotalCells?.forEach((v, i) => {
        const isSell = i >= 10;
        setCell2(COLS?.[i], r2, v, makeStyle(isSell ? NAVY_MID : NAVY, isSell ? goldValueFont : totalFont, i >= 6 ? 'right' : 'left', thickBorder));
      });
      merges2?.push({ s: { r: r2-1, c: 0 }, e: { r: r2-1, c: 8 } });
      r2 += 2;
    }

    // Grand total for sheet 2
    writeBanner(ws2, merges2, r2, `ENGINEERS + OUTSOURCE COST TOTAL: ${formatCurrency((engCostSum || 0) + (outCostSum || 0))}   ·   SELL TOTAL: ${formatCurrency((engSellSum || 0) + (outSellSum || 0))}`, NAVY, { bold: true, color: { rgb: GOLD }, sz: 11 }, 14, thickBorder);
    r2++;
    writeBanner(ws2, merges2, r2, `${COMPANY_NAME}   ·   CONFIDENTIAL — INTERNAL USE ONLY`, NAVY, { bold: true, color: { rgb: GOLD_LIGHT }, sz: 9 }, 14, thickBorder);

    ws2['!ref']    = XLSX?.utils?.encode_range({ s: { r: 0, c: 0 }, e: { r: r2, c: 13 } });
    ws2['!merges'] = merges2;
    ws2['!cols']   = [
      { wch: 22 }, { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 8 }, { wch: 8 },
      { wch: 20 }, { wch: 14 }, { wch: 16 }, { wch: 18 },
      { wch: 18 }, { wch: 22 }, { wch: 20 }, { wch: 4 },
    ];
    ws2['!rows']   = Array.from({ length: r2 + 1 }, (_, i) => ({ hpt: i < 2 ? 28 : 20 }));

    // ════════════════════════════════════════════════════════════════════════
    // SHEET 3 — MATERIALS & LICENSES
    // ════════════════════════════════════════════════════════════════════════
    const ws3 = {};
    const merges3 = [];
    let r3 = 1;

    const setCell3 = (col, row, value, style) => {
      ws3[`${col}${row}`] = { v: value, t: typeof value === 'number' ? 'n' : 's', s: style };
    };

    writeBanner(ws3, merges3, r3, COMPANY_NAME, NAVY, titleFont, 10, thickBorder);
    r3++;
    writeBanner(ws3, merges3, r3, 'MATERIALS & LICENSES — INTERNAL', NAVY, subTitleFont, 10, thickBorder);
    r3++;
    setCell3('A', r3, `${estimateTitle || '—'}   ·   ${ptcNumber || '—'}   ·   ${metaDate}`, makeStyle(NAVY_MID, metaFont, 'left', thickBorder));
    for (let c = 1; c < 10; c++) setCell3(COLS?.[c], r3, '', makeStyle(NAVY_MID, {}, 'left', thickBorder));
    merges3?.push({ s: { r: r3-1, c: 0 }, e: { r: r3-1, c: 9 } });
    r3++;
    for (let c = 0; c < 10; c++) setCell3(COLS?.[c], r3, '', {});
    r3++;

    // ── MATERIALS ─────────────────────────────────────────────────────────────
    if (matItems?.length > 0) {
      writeBanner(ws3, merges3, r3, '  ▶  MATERIALS', ORANGE, sectionBannerFont, 10, makeBorder(ORANGE));
      r3++;

      // ── GROUP HEADER ROW (COST / SELL) ──────────────────────────────────────
      // Cols 0-1 (A-B): identifier — navy, no label
      for (let c = 0; c < 2; c++) {
        setCell3(COLS?.[c], r3, '', makeStyle(NAVY, {}, 'center', thickBorder));
      }
      // Cols 2-5 (C-F): COST group — navy, white bold
      setCell3('C', r3, 'COST', makeStyle(NAVY, { bold: true, color: { rgb: WHITE }, sz: 11 }, 'center', thickBorder));
      for (let c = 3; c <= 5; c++) {
        setCell3(COLS?.[c], r3, '', makeStyle(NAVY, {}, 'center', thickBorder));
      }
      merges3?.push({ s: { r: r3-1, c: 2 }, e: { r: r3-1, c: 5 } });
      // Cols 6-7 (G-H): SELL group — gold, dark bold
      setCell3('G', r3, 'SELL', makeStyle(GOLD, { bold: true, color: { rgb: TEXT_DARK }, sz: 11 }, 'center', makeBorder(GOLD)));
      setCell3('H', r3, '', makeStyle(GOLD, {}, 'center', makeBorder(GOLD)));
      merges3?.push({ s: { r: r3-1, c: 6 }, e: { r: r3-1, c: 7 } });
      // Cols 8-9: spacers
      setCell3('I', r3, '', makeStyle(NAVY, {}, 'left', thickBorder));
      setCell3('J', r3, '', makeStyle(NAVY, {}, 'left', thickBorder));
      r3++;

      const matHeaders = [
        'Item Name', 'Unit', 'Qty', 'Waste %',
        'Unit Cost (OMR)', 'Cost Total (OMR)',
        'Unit Rate (OMR)', 'Line Sell Total (OMR)', '', '',
      ];
      matHeaders?.forEach((h, i) => {
        const isSell = i >= 6;
        setCell3(COLS?.[i], r3, h, makeStyle(isSell ? NAVY_MID : ORANGE, isSell ? sellHeaderFont : costHeaderFont, i >= 4 ? 'right' : 'left', thickBorder));
      });
      r3++;

      let totalMatCost = 0;
      let totalMatSell = 0;
      matItems?.forEach((m, idx) => {
        totalMatCost += m?.costTotal;
        totalMatSell += m?.sellTotal;
        const bg = idx % 2 === 0 ? WHITE : ORANGE_LIGHT;
        setCell3('A', r3, m?.name,                              makeStyle(bg, bodyFont,     'left',  border));
        setCell3('B', r3, '',                                   makeStyle(bg, bodyFont,     'left',  border));
        setCell3('C', r3, m?.qty,                               makeStyle(bg, bodyFont,     'center',border));
        setCell3('D', r3, `${m?.wastePct}%`,                    makeStyle(bg, bodyFont,     'center',border));
        setCell3('E', r3, formatCurrency(m?.unitCost),          makeStyle(bg, bodyFont,     'right', border));
        setCell3('F', r3, formatCurrency(m?.costTotal),         makeStyle(bg, boldBodyFont, 'right', border));
        setCell3('G', r3, formatCurrency(m?.sellUnitCost),      makeStyle(bg, sellValueFont,'right', border));
        setCell3('H', r3, formatCurrency(m?.sellTotal),         makeStyle(bg, sellValueFont,'right', border));
        setCell3('I', r3, '',                                   makeStyle(bg, {}, 'left', border));
        setCell3('J', r3, '',                                   makeStyle(bg, {}, 'left', border));
        r3++;
      });

      const matTotalCells = ['TOTAL', '', '', '', '', formatCurrency(totalMatCost), '', formatCurrency(totalMatSell), '', ''];
      matTotalCells?.forEach((v, i) => {
        const isSell = i >= 6;
        setCell3(COLS?.[i], r3, v, makeStyle(isSell ? NAVY_MID : ORANGE, isSell ? goldValueFont : totalFont, i >= 4 ? 'right' : 'left', thickBorder));
      });
      merges3?.push({ s: { r: r3-1, c: 0 }, e: { r: r3-1, c: 4 } });
      r3 += 2;
    }

    // ── LICENSES ──────────────────────────────────────────────────────────────
    if (licItems?.length > 0) {
      writeBanner(ws3, merges3, r3, '  ▶  SOFTWARE LICENSES', TEAL, sectionBannerFont, 10, makeBorder(TEAL));
      r3++;

      // ── GROUP HEADER ROW (COST / SELL) ──────────────────────────────────────
      for (let c = 0; c < 2; c++) {
        setCell3(COLS?.[c], r3, '', makeStyle(NAVY, {}, 'center', thickBorder));
      }
      setCell3('C', r3, 'COST', makeStyle(NAVY, { bold: true, color: { rgb: WHITE }, sz: 11 }, 'center', thickBorder));
      for (let c = 3; c <= 5; c++) {
        setCell3(COLS?.[c], r3, '', makeStyle(NAVY, {}, 'center', thickBorder));
      }
      merges3?.push({ s: { r: r3-1, c: 2 }, e: { r: r3-1, c: 5 } });
      setCell3('G', r3, 'SELL', makeStyle(GOLD, { bold: true, color: { rgb: TEXT_DARK }, sz: 11 }, 'center', makeBorder(GOLD)));
      setCell3('H', r3, '', makeStyle(GOLD, {}, 'center', makeBorder(GOLD)));
      merges3?.push({ s: { r: r3-1, c: 6 }, e: { r: r3-1, c: 7 } });
      setCell3('I', r3, '', makeStyle(NAVY, {}, 'left', thickBorder));
      setCell3('J', r3, '', makeStyle(NAVY, {}, 'left', thickBorder));
      r3++;

      const licHeaders = [
        'Software Name', 'Type', 'Qty', 'Duration (mo)',
        'Unit Cost (OMR)', 'Cost Total (OMR)',
        'Unit Rate (OMR)', 'Line Sell Total (OMR)', '', '',
      ];
      licHeaders?.forEach((h, i) => {
        const isSell = i >= 6;
        setCell3(COLS?.[i], r3, h, makeStyle(isSell ? NAVY_MID : TEAL, isSell ? sellHeaderFont : costHeaderFont, i >= 4 ? 'right' : 'left', thickBorder));
      });
      r3++;

      let totalLicCost = 0;
      let totalLicSell = 0;
      licItems?.forEach((l, idx) => {
        totalLicCost += l?.costTotal;
        totalLicSell += l?.sellTotal;
        const bg = idx % 2 === 0 ? WHITE : TEAL_LIGHT;
        setCell3('A', r3, l?.name,                              makeStyle(bg, bodyFont,     'left',  border));
        setCell3('B', r3, l?.type,                              makeStyle(bg, bodyFont,     'left',  border));
        setCell3('C', r3, l?.qty,                               makeStyle(bg, bodyFont,     'center',border));
        setCell3('D', r3, l?.duration,                          makeStyle(bg, bodyFont,     'center',border));
        setCell3('E', r3, formatCurrency(l?.unitCostRaw),       makeStyle(bg, bodyFont,     'right', border));
        setCell3('F', r3, formatCurrency(l?.costTotal),         makeStyle(bg, boldBodyFont, 'right', border));
        setCell3('G', r3, formatCurrency(l?.sellUnitCost),      makeStyle(bg, sellValueFont,'right', border));
        setCell3('H', r3, formatCurrency(l?.sellTotal),         makeStyle(bg, sellValueFont,'right', border));
        setCell3('I', r3, '',                                   makeStyle(bg, {}, 'left', border));
        setCell3('J', r3, '',                                   makeStyle(bg, {}, 'left', border));
        r3++;
      });

      const licTotalCells = ['TOTAL', '', '', '', '', formatCurrency(totalLicCost), '', formatCurrency(totalLicSell), '', ''];
      licTotalCells?.forEach((v, i) => {
        const isSell = i >= 6;
        setCell3(COLS?.[i], r3, v, makeStyle(isSell ? NAVY_MID : TEAL, isSell ? goldValueFont : totalFont, i >= 4 ? 'right' : 'left', thickBorder));
      });
      merges3?.push({ s: { r: r3-1, c: 0 }, e: { r: r3-1, c: 4 } });
      r3 += 2;
    }

    // Grand total for sheet 3
    writeBanner(ws3, merges3, r3, `MATERIALS + LICENSES COST TOTAL: ${formatCurrency((matCostSum || 0) + (licCostSum || 0))}   ·   SELL TOTAL: ${formatCurrency((matSellSum || 0) + (licSellSum || 0))}`, NAVY, { bold: true, color: { rgb: GOLD }, sz: 11 }, 10, thickBorder);
    r3++;
    writeBanner(ws3, merges3, r3, `${COMPANY_NAME}   ·   CONFIDENTIAL — INTERNAL USE ONLY`, NAVY, { bold: true, color: { rgb: GOLD_LIGHT }, sz: 9 }, 10, thickBorder);

    ws3['!ref']    = XLSX?.utils?.encode_range({ s: { r: 0, c: 0 }, e: { r: r3, c: 9 } });
    ws3['!merges'] = merges3;
    ws3['!cols']   = [
      { wch: 28 }, { wch: 16 }, { wch: 8 }, { wch: 14 },
      { wch: 18 }, { wch: 18 },
      { wch: 18 }, { wch: 20 }, { wch: 4 }, { wch: 4 },
    ];
    ws3['!rows']   = Array.from({ length: r3 + 1 }, (_, i) => ({ hpt: i < 2 ? 28 : 20 }));

    // ════════════════════════════════════════════════════════════════════════
    // SHEET 4 — PROFIT DISTRIBUTION
    // ════════════════════════════════════════════════════════════════════════
    const ws4 = {};
    const merges4 = [];
    let r4 = 1;

    const setCell4 = (col, row, value, style) => {
      ws4[`${col}${row}`] = { v: value, t: typeof value === 'number' ? 'n' : 's', s: style };
    };

    writeBanner(ws4, merges4, r4, COMPANY_NAME, NAVY, titleFont, 6, thickBorder);
    r4++;
    writeBanner(ws4, merges4, r4, 'PROFIT DISTRIBUTION — INTERNAL', NAVY, subTitleFont, 6, thickBorder);
    r4++;
    setCell4('A', r4, `${estimateTitle || '—'}   ·   ${ptcNumber || '—'}   ·   ${metaDate}`, makeStyle(NAVY_MID, metaFont, 'left', thickBorder));
    for (let c = 1; c < 6; c++) setCell4(COLS?.[c], r4, '', makeStyle(NAVY_MID, {}, 'left', thickBorder));
    merges4?.push({ s: { r: r4-1, c: 0 }, e: { r: r4-1, c: 5 } });
    r4++;
    for (let c = 0; c < 6; c++) setCell4(COLS?.[c], r4, '', {});
    r4++;

    // Financial Summary KPI block
    writeBanner(ws4, merges4, r4, '  FINANCIAL SUMMARY', NAVY, sectionBannerFont, 6, thickBorder);
    r4++;

    const finRows = [
      { label: 'Total Cost (OMR)',      value: formatCurrency(totalCost),      fill: NAVY,    font: { bold: true, color: { rgb: WHITE }, sz: 10 } },
      { label: 'Sell Price (OMR)',       value: formatCurrency(sellPrice),      fill: NAVY_MID,font: { bold: true, color: { rgb: GOLD }, sz: 10 } },
      { label: 'Gross Profit (OMR)',     value: formatCurrency(grossProfit),    fill: GREEN,   font: { bold: true, color: { rgb: WHITE }, sz: 10 } },
      { label: 'Profit Margin %',        value: `${profitMarginPct?.toFixed(2)}%`, fill: PURPLE, font: { bold: true, color: { rgb: WHITE }, sz: 10 } },
      { label: `Overhead (${calculations?.overheadPercentage || 0}%)`, value: formatCurrency(overheadCosts), fill: ORANGE, font: { bold: true, color: { rgb: WHITE }, sz: 10 } },
      { label: `Contingency (${calculations?.contingencyPercentage || 0}%)`, value: formatCurrency(contingencyCosts), fill: TEAL, font: { bold: true, color: { rgb: WHITE }, sz: 10 } },
    ];
    finRows?.forEach((row, i) => {
      setCell4(COLS?.[i], r4, row?.label, makeStyle(row?.fill, { bold: true, color: { rgb: i < 2 ? GOLD_LIGHT : 'FFFFFF' }, sz: 9 }, 'center', makeBorder(row?.fill)));
    });
    r4++;
    finRows?.forEach((row, i) => {
      setCell4(COLS?.[i], r4, row?.value, makeStyle(row?.fill, row?.font, 'center', makeBorder(row?.fill)));
    });
    r4 += 2;

    // Per-section sell price breakdown
    writeBanner(ws4, merges4, r4, '  SELL PRICE BREAKDOWN BY SECTION', NAVY, sectionBannerFont, 6, thickBorder);
    r4++;

    // Header
    ['Section', 'Sell Price (OMR)', '% of Total Sell', 'Cost (OMR)', 'Gross Profit (OMR)', 'Margin %']?.forEach((h, i) => {
      setCell4(COLS?.[i], r4, h, makeStyle(NAVY_MID, navyHeaderFont, i >= 1 ? 'right' : 'left', thickBorder));
    });
    r4++;

    const sectionBreakdown = [];
    if (engItems?.length > 0)  sectionBreakdown?.push({ label: 'Engineers',          cost: engCostSum,  sell: engSellSum  });
    if (matItems?.length > 0)  sectionBreakdown?.push({ label: 'Materials',           cost: matCostSum,  sell: matSellSum  });
    if (licItems?.length > 0)  sectionBreakdown?.push({ label: 'Licenses',            cost: licCostSum,  sell: licSellSum  });
    if (outItems?.length > 0)  sectionBreakdown?.push({ label: 'Outsource Resources', cost: outCostSum,  sell: outSellSum  });

    sectionBreakdown?.forEach((row, idx) => {
      const bg = idx % 2 === 0 ? WHITE : LIGHT_GREY;
      const pct = sellPrice > 0 ? ((row?.sell / sellPrice) * 100)?.toFixed(1) : '0.0';
      const gp  = row?.sell - row?.cost;
      const mg  = row?.sell > 0 ? ((gp / row?.sell) * 100)?.toFixed(1) : '0.0';
      setCell4('A', r4, row?.label,              makeStyle(bg, boldBodyFont, 'left',  border));
      setCell4('B', r4, formatCurrency(row?.sell), makeStyle(bg, sellValueFont, 'right', border));
      setCell4('C', r4, `${pct}%`,               makeStyle(bg, bodyFont,     'right', border));
      setCell4('D', r4, formatCurrency(row?.cost), makeStyle(bg, bodyFont,   'right', border));
      setCell4('E', r4, formatCurrency(gp),       makeStyle(bg, greenValueFont, 'right', border));
      setCell4('F', r4, `${mg}%`,                 makeStyle(bg, bodyFont,     'right', border));
      r4++;
    });

    // Grand total row
    const overallGP = sellPrice - totalCost;
    const overallMg = sellPrice > 0 ? ((overallGP / sellPrice) * 100)?.toFixed(1) : '0.0';
    setCell4('A', r4, 'GRAND TOTAL',              makeStyle(NAVY, totalFont,      'left',  thickBorder));
    setCell4('B', r4, formatCurrency(sellPrice),   makeStyle(NAVY, goldValueFont,  'right', thickBorder));
    setCell4('C', r4, '100.0%',                    makeStyle(NAVY, totalFont,      'right', thickBorder));
    setCell4('D', r4, formatCurrency(totalCost),   makeStyle(NAVY, totalFont,      'right', thickBorder));
    setCell4('E', r4, formatCurrency(overallGP),   makeStyle(GREEN, { bold: true, color: { rgb: WHITE }, sz: 10 }, 'right', makeBorder(GREEN)));
    setCell4('F', r4, `${overallMg}%`,             makeStyle(PURPLE, { bold: true, color: { rgb: WHITE }, sz: 10 }, 'right', makeBorder(PURPLE)));
    r4 += 2;

    writeBanner(ws4, merges4, r4, `${COMPANY_NAME}   ·   CONFIDENTIAL — INTERNAL USE ONLY`, NAVY, { bold: true, color: { rgb: GOLD }, sz: 9 }, 6, thickBorder);

    ws4['!ref']    = XLSX?.utils?.encode_range({ s: { r: 0, c: 0 }, e: { r: r4, c: 5 } });
    ws4['!merges'] = merges4;
    ws4['!cols']   = [{ wch: 28 }, { wch: 20 }, { wch: 16 }, { wch: 20 }, { wch: 20 }, { wch: 14 }];
    ws4['!rows']   = Array.from({ length: r4 + 1 }, (_, i) => ({ hpt: i < 2 ? 28 : 22 }));

    // Assemble workbook
    XLSX?.utils?.book_append_sheet(wb, ws1, 'Executive Summary');
    XLSX?.utils?.book_append_sheet(wb, ws2, 'Engineers & Outsource');
    XLSX?.utils?.book_append_sheet(wb, ws3, 'Materials & Licenses');
    XLSX?.utils?.book_append_sheet(wb, ws4, 'Profit Distribution');

    XLSX?.writeFile(wb, `Internal_${(estimateTitle || 'Estimate')?.replace(/\s+/g, '_')}_${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);
    setIsExporting(false);
  };

  const handleExport = async () => {
    if (!validate()) return;
    setIsExporting(true);
    try {
      if (exportMode === 'customer') {
        if (exportFormat === 'pdf') await handleExportCustomerPDF();
        else if (exportFormat === 'excel') handleExportCustomerExcel();
      } else {
        if (exportFormat === 'pdf') await handleExportPDF();
        else if (exportFormat === 'excel') handleExportExcel();
        else if (exportFormat === 'word') await handleExportWord();
      }
      onClose();
    } catch (err) {
      console.error('Export error:', err);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const formats = [
    { id: 'pdf', label: 'PDF Document', icon: 'FileText', desc: exportMode === 'customer' ? '3-page quotation: Cover, Scope of Services, Summary' : 'Professional formatted report with tables and branding', ext: '.pdf' },
    { id: 'excel', label: 'Excel Spreadsheet', icon: 'Table', desc: exportMode === 'customer' ? '2-sheet workbook: Quotation Summary, Itemized Scope' : 'Multi-sheet workbook with summary, engineers, materials & licenses', ext: '.xlsx' },
    ...(exportMode === 'internal' ? [{ id: 'word', label: 'Word Document', icon: 'FileType', desc: 'Formatted document with tables, ready for editing', ext: '.docx' }] : []),
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl animate-fade-in max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-primary to-accent">
              <Icon name="Download" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Export Results</h2>
              <p className="text-xs text-muted-foreground">Choose format and download your estimate</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={18} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Export Mode Toggle */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Export Version</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { setExportMode('internal'); if (exportFormat === 'word') setExportFormat('pdf'); }}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                  exportMode === 'internal' ?'border-primary bg-primary/5' :'border-border hover:border-primary/40 hover:bg-muted/50'
                }`}
              >
                <div className={`p-1.5 rounded-lg ${exportMode === 'internal' ? 'bg-primary/20' : 'bg-muted'}`}>
                  <Icon name="Building2" size={16} color={exportMode === 'internal' ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
                </div>
                <div>
                  <p className={`text-sm font-semibold ${exportMode === 'internal' ? 'text-primary' : 'text-foreground'}`}>Internal</p>
                  <p className="text-xs text-muted-foreground">Full cost, margin & profit data</p>
                </div>
                {exportMode === 'internal' && <Icon name="CheckCircle2" size={16} color="var(--color-primary)" className="ml-auto" />}
              </button>
              <button
                onClick={() => { setExportMode('customer'); if (exportFormat === 'word') setExportFormat('pdf'); }}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                  exportMode === 'customer' ?'border-success bg-success/5' :'border-border hover:border-success/40 hover:bg-muted/50'
                }`}
              >
                <div className={`p-1.5 rounded-lg ${exportMode === 'customer' ? 'bg-success/20' : 'bg-muted'}`}>
                  <Icon name="Users" size={16} color={exportMode === 'customer' ? 'var(--color-success)' : 'var(--color-muted-foreground)'} />
                </div>
                <div>
                  <p className={`text-sm font-semibold ${exportMode === 'customer' ? 'text-success' : 'text-foreground'}`}>Customer</p>
                  <p className="text-xs text-muted-foreground">Sell prices only, no internal data</p>
                </div>
                {exportMode === 'customer' && <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="ml-auto" />}
              </button>
            </div>
            {exportMode === 'customer' && (
              <div className="mt-2 flex items-start gap-2 p-2.5 bg-success/10 border border-success/30 rounded-lg">
                <Icon name="ShieldCheck" size={14} color="var(--color-success)" className="mt-0.5 shrink-0" />
                <p className="text-xs text-success">Customer version: sell prices only — no cost, margin, or profit data. Health Insurance breakdown is included.</p>
              </div>
            )}
          </div>

          {/* Auto-populated notice */}
          {(savedPtcName || savedPtcNumber) && (
            <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/30 rounded-lg">
              <Icon name="CheckCircle2" size={15} color="var(--color-success)" />
              <p className="text-xs text-success font-medium">PTC details auto-populated from the last saved estimate.</p>
            </div>
          )}
          {(!savedPtcName || !savedPtcNumber) && (
            <div className="flex items-center gap-2 p-3 bg-warning/10 border border-warning/30 rounded-lg">
              <Icon name="AlertTriangle" size={15} color="var(--color-warning)" />
              <p className="text-xs text-warning font-medium">Save the estimate first to auto-populate PTC Name and PTC Number before exporting.</p>
            </div>
          )}

          {/* Validation errors banner */}
          {validationErrors?.length > 0 && (
            <div className="p-3 bg-error/10 border border-error/30 rounded-lg space-y-1">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={15} color="var(--color-error)" />
                <p className="text-xs text-error font-semibold">Calculation mismatch detected — export blocked</p>
              </div>
              {validationErrors?.map((err, i) => (
                <p key={i} className="text-xs text-error ml-5">{err}</p>
              ))}
              <p className="text-xs text-muted-foreground ml-5 mt-1">Please review the calculator inputs and recalculate before exporting.</p>
            </div>
          )}

          {/* Title Input */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">PTC Name <span className="text-error">*</span></label>
            <div className="relative">
              <input
                type="text"
                value={estimateTitle}
                readOnly
                className={`w-full px-3 py-2 bg-muted/50 border rounded-lg text-sm text-foreground cursor-not-allowed ${errors?.estimateTitle ? 'border-error' : 'border-border'}`}
                placeholder="Auto-populated from saved estimate..."
              />
              {estimateTitle && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Icon name="Lock" size={13} color="var(--color-muted-foreground)" />
                </span>
              )}
            </div>
            {errors?.estimateTitle && <p className="text-xs text-error mt-1">{errors?.estimateTitle}</p>}
          </div>

          {/* Header Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Estimate No.</label>
              <input
                type="text"
                value={estimateNo}
                onChange={e => setEstimateNo(e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="e.g. EST-001"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">PTC Number <span className="text-error">*</span></label>
              <div className="relative">
                <input
                  type="text"
                  value={ptcNumber}
                  readOnly
                  className={`w-full px-3 py-2 bg-muted/50 border rounded-lg text-sm text-foreground cursor-not-allowed ${errors?.ptcNumber ? 'border-error' : 'border-border'}`}
                  placeholder="Auto-populated from saved estimate..."
                />
                {ptcNumber && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Icon name="Lock" size={13} color="var(--color-muted-foreground)" />
                  </span>
                )}
              </div>
              {errors?.ptcNumber && <p className="text-xs text-error mt-1">{errors?.ptcNumber}</p>}
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">Date</label>
              <input
                type="date"
                value={estimateDate}
                onChange={e => setEstimateDate(e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>

          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">Export Format</label>
            <div className="space-y-2">
              {formats?.map(fmt => (
                <button
                  key={fmt?.id}
                  onClick={() => setExportFormat(fmt?.id)}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                    exportFormat === fmt?.id
                      ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40 hover:bg-muted/50'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${exportFormat === fmt?.id ? 'bg-primary/20' : 'bg-muted'}`}>
                    <Icon name={fmt?.icon} size={20} color={exportFormat === fmt?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${exportFormat === fmt?.id ? 'text-primary' : 'text-foreground'}`}>{fmt?.label}</span>
                      <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{fmt?.ext}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{fmt?.desc}</p>
                  </div>
                  {exportFormat === fmt?.id && (
                    <Icon name="CheckCircle2" size={18} color="var(--color-primary)" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* What's included */}
          <div className="bg-muted/50 rounded-xl p-4">
            <p className="text-xs font-semibold text-foreground mb-2">
              {exportMode === 'customer' ? 'Customer version includes:' : 'Included in export:'}
            </p>
            <div className="grid grid-cols-2 gap-1">
              {(exportMode === 'customer' ? [
                exportFormat === 'pdf' ? 'Quotation Cover Page' : 'Quotation Summary Sheet',
                exportFormat === 'pdf' ? 'Scope of Services' : 'Itemized Scope Sheet',
                exportFormat === 'pdf' ? 'Quotation Summary' : 'Section Subtotals',
                `${selectedEngineers?.length || 0} Engineers (sell price)`,
                `${selectedMaterials?.length || 0} Materials (sell price)`,
                `${selectedLicenses?.length || 0} Licenses (sell price)`,
                ...(selectedOutsource?.length > 0 ? [`${selectedOutsource?.length} Outsource (sell price)`] : []),
                'Health Insurance breakdown',
                'Terms & Conditions',
              ] : [
                'Calculation Summary',
                `${selectedEngineers?.length || 0} Engineers`,
                `${selectedMaterials?.length || 0} Materials`,
                `${selectedLicenses?.length || 0} Licenses`,
                ...(selectedOutsource?.length > 0 ? [`${selectedOutsource?.length} Outsource Resource${selectedOutsource?.length > 1 ? 's' : ''}`] : []),
                'Cost Breakdown',
                'Pricing & Profit',
                'Profit Distribution',
              ])?.map(item => (
                <div key={item} className="flex items-center gap-1.5">
                  <Icon name="Check" size={12} color="var(--color-success)" />
                  <span className="text-xs text-muted-foreground">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border sticky bottom-0 bg-card">
          <Button variant="outline" onClick={onClose} disabled={isExporting}>Cancel</Button>
          <Button
            variant="default"
            iconName={isExporting ? 'Loader2' : 'Download'}
            iconPosition="left"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : `Export ${formats?.find(f => f?.id === exportFormat)?.ext}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
